/* eslint-disable no-use-before-define */
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Container from '@material-ui/core/Container';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import SearchIcon from '@material-ui/icons/Search';

import CockpitNavigation from './CockpitNavigation';
import {
  chargerInvestigationCamera,
  chargerSnapshotCamera,
  lireTracesLocalesCockpit,
  rechercherInvestigation,
  reprojeterSnapshotCamera,
  telechargerSyncCamLogJournee,
  tracerUsageCockpit,
} from './cockpitApi';
import {
  chargerSignauxDocumentaires,
  creerIndexSignauxVide,
  listerSignauxReconnus,
  trouverSignalDansTexte,
} from './signauxDocumentairesService';
import { decrireTemporaliteEpisode } from './episodeTemporalite';

const CLE_RAPPORT_REPROJECTION = 'syncstats.cockpit.rapportReprojection.v1';

const useStyles = makeStyles((theme) => ({
  page: { paddingTop: theme.spacing(3), paddingBottom: theme.spacing(4), textAlign: 'left' },
  paper: { padding: theme.spacing(2), overflowX: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', gap: theme.spacing(2), flexWrap: 'wrap', alignItems: 'center' },
  recherche: { display: 'flex', gap: theme.spacing(1), flexWrap: 'wrap', marginTop: theme.spacing(2) },
  section: { marginTop: theme.spacing(2) },
  muted: { color: '#666' },
  denseList: { margin: 0, paddingLeft: theme.spacing(2.5) },
  callout: { borderLeft: '4px solid #0066ff', padding: theme.spacing(1.5), backgroundColor: '#f8fbff' },
  warning: { borderLeft: '4px solid #f59e0b', padding: theme.spacing(1.5), backgroundColor: '#fff7ed' },
  signalDocumentaire: { borderLeft: '4px solid #dc2626', padding: theme.spacing(1.25), backgroundColor: '#fef2f2', marginTop: theme.spacing(1), marginBottom: theme.spacing(1) },
  signalDocumentaireTitre: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(0.75), alignItems: 'center', marginBottom: theme.spacing(0.75) },
  grilleSignalDocumentaire: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: theme.spacing(1), marginTop: theme.spacing(1) },
  focusCamera: { border: '1px solid #bfdbfe', backgroundColor: '#eff6ff', borderRadius: 6, padding: theme.spacing(1.5) },
  rowClickable: { cursor: 'pointer' },
  selectedRow: { backgroundColor: '#eef5ff' },
  pre: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, backgroundColor: '#111827', color: '#f9fafb', padding: theme.spacing(1), borderRadius: 4 },
  preClair: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word', fontSize: 12, backgroundColor: '#f9fafb', color: '#111827', padding: theme.spacing(1), borderRadius: 4, border: '1px solid #e5e7eb' },
  table: { minWidth: 760 },
  chipNormal: { backgroundColor: '#ecfdf5', color: '#065f46' },
  chipWatch: { backgroundColor: '#fff7ed', color: '#9a3412' },
  chipBlocked: { backgroundColor: '#fef2f2', color: '#991b1b' },
  chipUnknown: { backgroundColor: '#f3f4f6', color: '#374151' },
  ligneTimeline: { display: 'flex', gap: 16, paddingTop: 3, paddingBottom: 3 },
  heureTimeline: { color: '#666', minWidth: 44, fontFamily: 'monospace', fontSize: 13 },
  signalTimeline: { fontFamily: 'monospace', fontSize: 13 },
  timelineCamera: { display: 'grid', gridTemplateColumns: '150px 64px minmax(140px, 1fr) minmax(140px, 1fr) minmax(0, 2.4fr)', gap: theme.spacing(1), paddingTop: 6, paddingBottom: 6, borderBottom: '1px solid #e5e7eb', alignItems: 'start', [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' } },
  timelineCell: { minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' },
  filtresInvestigation: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: theme.spacing(1), marginTop: theme.spacing(1.5), alignItems: 'center' },
  barreActions: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1), alignItems: 'center', marginTop: theme.spacing(1) },
  episode: { border: '1px solid #e5e7eb', borderRadius: 6, padding: theme.spacing(1.25), marginBottom: theme.spacing(1), backgroundColor: '#fff' },
  episodeSelectionne: { borderColor: '#2563eb', backgroundColor: '#eff6ff' },
  episodeHeader: { display: 'grid', gridTemplateColumns: 'minmax(180px, 1fr) auto', gap: theme.spacing(1), alignItems: 'start', cursor: 'pointer', [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' } },
  episodeTitre: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(0.75), alignItems: 'center' },
  episodeTemporalite: { display: 'inline-flex', flexDirection: 'column', flexShrink: 0, fontFamily: 'monospace', lineHeight: 1.35 },
  episodeIndicateurTemporel: { color: '#4b5563', fontSize: 12, marginTop: theme.spacing(0.5) },
  episodeMeta: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(0.75), marginTop: theme.spacing(0.75), color: '#6b7280', fontSize: 12 },
  repetitions: { backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 4, padding: theme.spacing(0.75), marginTop: theme.spacing(1) },
  ligneCompacte: { display: 'grid', gridTemplateColumns: '54px minmax(150px, 1fr) minmax(0, 2fr) 96px', gap: theme.spacing(1), paddingTop: 6, paddingBottom: 6, borderTop: '1px solid #edf2f7', alignItems: 'start', [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' } },
  texteCompact: { minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word', lineHeight: 1.35 },
  zoneBrute: { maxHeight: 460, overflowY: 'auto', marginTop: theme.spacing(1) },
  tableMini: { marginTop: theme.spacing(1), '& td': { verticalAlign: 'top' } },
  fluxRelation: { display: 'flex', alignItems: 'center', gap: 8, paddingTop: 3, paddingBottom: 3, fontFamily: 'monospace', fontSize: 13 },
  fleche: { color: '#0066ff', fontWeight: 'bold' },
  labelCarte: { width: 220, verticalAlign: 'top' },
  boucleDetectee: { backgroundColor: '#f0fdf4', border: '1px solid #16a34a', borderRadius: 4, padding: '8px 10px', marginBottom: 6 },
  boucleDetecteeTitre: { fontWeight: 700, color: '#15803d', marginBottom: 2 },
  fluxImpacte: { backgroundColor: '#eff6ff', border: '1px solid #2563eb', borderRadius: 4, padding: '8px 10px', marginBottom: 6 },
  fluxImpacteTitre: { fontWeight: 700, color: '#1d4ed8', marginBottom: 2 },
  grilleSignalLarge: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginTop: 8 },
  carteSignalAnalyse: { border: '1px solid #fca5a5', backgroundColor: '#fff7f7', borderRadius: 6, padding: theme.spacing(1.25), marginBottom: 6 },
  carteBoucleAnalyse: { border: '1px solid #16a34a', backgroundColor: '#f0fdf4', borderRadius: 6, padding: theme.spacing(1.25), marginBottom: 6 },
  carteFluxAnalyse: { border: '1px solid #2563eb', backgroundColor: '#eff6ff', borderRadius: 6, padding: theme.spacing(1.25), marginBottom: 6 },
  carteGrille: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: theme.spacing(1), marginTop: theme.spacing(0.75) },
  carteActions: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: theme.spacing(0.75) },
}));

const CockpitInvestigation = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const [requete, setRequete] = useState('');
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState('');
  const [endpointErreur, setEndpointErreur] = useState('');
  const [rechercheEffectuee, setRechercheEffectuee] = useState(false);
  const [payloadInvestigation, setPayloadInvestigation] = useState(null);
  const [payloadCamera, setPayloadCamera] = useState(null);
  const [contexteCamera, setContexteCamera] = useState({});
  const [resultats, setResultats] = useState([]);
  const [selectionId, setSelectionId] = useState('');
  const [tracesLocales, setTracesLocales] = useState([]);
  const [debugVisible, setDebugVisible] = useState(false);
  const [indexSignauxDocumentaires, setIndexSignauxDocumentaires] = useState(creerIndexSignauxVide());

  useEffect(() => {
    let actif = true;
    chargerSignauxDocumentaires()
      .then((index) => {
        if (actif) setIndexSignauxDocumentaires(index);
      })
      .catch(() => {
        if (actif) setIndexSignauxDocumentaires(creerIndexSignauxVide());
      });
    return () => {
      actif = false;
    };
  }, []);

  const executerInvestigationCamera = ({ camera, journee = '', incident = '', debut = '', fin = '', event = '', episode = '', origine = 'direct' }) => {
    const cameraNormalisee = String(camera || '').trim();
    if (!cameraNormalisee) return;
    const contexte = { camera: cameraNormalisee, journee, incident, debut, fin, event, episode, origine };
    setRequete(cameraNormalisee);
    setContexteCamera(contexte);
    setChargement(true);
    setErreur('');
    setEndpointErreur('/monitoring/camera/investigation?camera=...');
    setRechercheEffectuee(true);
    setPayloadCamera(null);
    setPayloadInvestigation(null);
    setResultats([]);
    setSelectionId('');
    tracerUsageCockpit('investigation_camera', contexte);

    chargerInvestigationCamera({ camera: cameraNormalisee, journee, incident, debut, fin, event, episode })
      .then((payload) => {
        setPayloadCamera(payload);
        setTracesLocales(lireTracesLocalesCockpit());
        setChargement(false);
      })
      .catch((erreurRecherche) => {
        setErreur(erreurRecherche.message);
        setEndpointErreur('/monitoring/camera/investigation?camera=...');
        setTracesLocales(lireTracesLocalesCockpit());
        setChargement(false);
      });
  };

  const executerRecherche = (recherche) => {
    const q = String(recherche || '').trim();
    if (!q) return;
    const cameraRecherche = extraireCameraDepuisRecherche(q);
    if (cameraRecherche) {
      history.replace(`/cockpit/investigation?camera=${encodeURIComponent(cameraRecherche)}`);
      executerInvestigationCamera({ camera: cameraRecherche, origine: 'champ_recherche' });
      return;
    }

    setRequete(q);
    setChargement(true);
    setErreur('');
    setEndpointErreur('/monitoring/graphe/recherche?q=...');
    setRechercheEffectuee(true);
    setPayloadInvestigation(null);
    setPayloadCamera(null);
    setContexteCamera({});
    setResultats([]);
    setSelectionId('');
    tracerUsageCockpit('recherche', { requete: q });

    rechercherInvestigation({ requete: q, profondeur: 4 })
      .then((payload) => {
        const prochainsResultats = payload.resultats || [];
        setPayloadInvestigation(payload);
        setResultats(prochainsResultats);
        setSelectionId(prochainsResultats[0] ? prochainsResultats[0].id : '');
        setTracesLocales(lireTracesLocalesCockpit());
        tracerUsageCockpit('resultats_recus', {
          requete: q,
          nombre: prochainsResultats.length,
          versionContrat: payload.versionContrat,
          metriques: payload.metriques,
          premiersObjets: prochainsResultats.slice(0, 5).map((r) => r.id),
        });
        setChargement(false);
      })
      .catch((erreurRecherche) => {
        setErreur(erreurRecherche.message);
        setEndpointErreur('/monitoring/graphe/recherche?q=...');
        setTracesLocales(lireTracesLocalesCockpit());
        setChargement(false);
      });
  };

  useEffect(() => {
    setTracesLocales(lireTracesLocalesCockpit());
    const params = new URLSearchParams(location.search);
    const camera = params.get('camera') || '';
    const q = params.get('q') || '';
    if (camera) {
      const journee = params.get('journee') || '';
      const incident = params.get('incident') || '';
      const debut = params.get('debut') || '';
      const fin = params.get('fin') || '';
      const event = params.get('event') || '';
      const episode = params.get('episode') || '';
      executerInvestigationCamera({ camera, journee, incident, debut, fin, event, episode, origine: 'url_camera' });
      return;
    }
    if (q) executerRecherche(q);
  }, [location.search]);

  const selection = useMemo(() => (
    resultats.find((r) => r.id === selectionId) || resultats[0] || null
  ), [resultats, selectionId]);

  const lancerRecherche = (event) => {
    if (event) event.preventDefault();
    executerRecherche(requete.trim());
  };

  const choisirResultat = (resultat) => {
    setSelectionId(resultat.id);
    tracerUsageCockpit('objet_consulte', {
      objetId: resultat.id,
      type: resultat.racine && resultat.racine.type,
      timelineItems: (resultat.timelineOrdonnee || []).length,
      anomalies: (resultat.anomaliesStagnations || []).length,
      relationsManquantes: (resultat.relationsManquantes || []).length,
    });
    setTracesLocales(lireTracesLocalesCockpit());
  };

  return (
    <Container maxWidth={false} className={classes.page}>
      <CockpitNavigation vueActive="investigation" />
      <Grid container spacing={3}>

        <Grid item xs={12}>
          <Paper className={classes.paper}>
            <div className={classes.header}>
              <div>
                <Typography variant="h4">Investigation detaillee</Typography>
                <Typography variant="body2" className={classes.muted}>
                  Recherche approfondie. Le backend reste la source de verite.
                </Typography>
              </div>
              <Button
                variant="text"
                size="small"
                onClick={() => history.push('/cockpit')}
              >
                ← Cockpit operationnel
              </Button>
            </div>
            <form className={classes.recherche} onSubmit={lancerRecherche}>
              <TextField
                label="Match, camera, video, incident, version, etat 202..."
                variant="outlined"
                size="small"
                value={requete}
                onChange={(e) => setRequete(e.target.value)}
                style={{ minWidth: 360, maxWidth: 560, flex: '1 1 360px' }}
              />
              <Button type="submit" variant="contained" color="primary" startIcon={<SearchIcon />} disabled={chargement}>
                Rechercher
              </Button>
              <Button variant="outlined" onClick={() => setRequete('etat_202')}>etat 202</Button>
              <Button variant="outlined" onClick={() => setRequete('VIDEO_INCIDENT')}>VIDEO_INCIDENT</Button>
              <Button variant="outlined" onClick={() => setRequete('camera_96')}>camera 96</Button>
            </form>

            {chargement && <Typography className={classes.section}>Recherche en cours...</Typography>}
            {erreur && (
              <div className={classes.warning + ' ' + classes.section}>
                <Typography variant="subtitle1">Erreur backend investigation</Typography>
                <Typography variant="body2">
                  {erreur}{erreur.endsWith('.') ? '' : '.'} Endpoint attendu: {endpointErreur || '/monitoring/graphe/recherche?q=...'}
                </Typography>
              </div>
            )}
            {!chargement && !erreur && rechercheEffectuee && !payloadCamera && resultats.length === 0 && (
              <div className={classes.callout + ' ' + classes.section}>
                <Typography variant="subtitle1">Aucun resultat</Typography>
                <Typography variant="body2">
                  La recherche a repondu correctement, mais aucun objet retourne.
                </Typography>
              </div>
            )}
          </Paper>
        </Grid>

        {resultats.length > 1 && (
          <Grid item xs={12}>
            <Paper className={classes.paper}>
              <Typography variant="h6">Resultats ({resultats.length})</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Objet</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Etats</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resultats.map((r) => (
                    <TableRow
                      hover
                      key={r.id}
                      className={(selection && selection.id === r.id ? classes.selectedRow + ' ' : '') + classes.rowClickable}
                      onClick={() => choisirResultat(r)}
                    >
                      <TableCell>{r.racine.libelle || r.id}</TableCell>
                      <TableCell>{r.racine.type || 'n/d'}</TableCell>
                      <TableCell>{texte((r.racine.etats || []).join(', '))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Grid>
        )}

        {selection && (
          <Grid item xs={12}>
            <Paper className={classes.paper}>
              <Typography variant="h5">{selection.racine.libelle || selection.id}</Typography>
              <Typography variant="body2" className={classes.muted}>
                {selection.racine.type || 'Objet runtime'} | {selection.id}
              </Typography>

              <SectionPreuveBruteImmediate resultat={selection} classes={classes} indexSignauxDocumentaires={indexSignauxDocumentaires} />
              <SectionCarteObjet resultat={selection} classes={classes} />
              <SectionTimelineHumaine items={selection.timelineOrdonnee} classes={classes} />
              <SectionRelationsVisibles
                voisinage={selection.voisinageRelationnel}
                relationsManquantes={selection.relationsManquantes}
                classes={classes}
              />
            </Paper>
          </Grid>
        )}

        {payloadCamera && (
          <Grid item xs={12}>
            <Paper className={classes.paper}>
              <SectionInvestigationCamera
                payload={payloadCamera}
                contexte={contexteCamera}
                classes={classes}
                indexSignauxDocumentaires={indexSignauxDocumentaires}
                onReprojectionTerminee={() => executerInvestigationCamera(contexteCamera)}
              />
            </Paper>
          </Grid>
        )}

        {rechercheEffectuee && (
          <Grid item xs={12}>
            <Paper className={classes.paper}>
              <Button size="small" variant="text" onClick={() => setDebugVisible(!debugVisible)}>
                {debugVisible ? '▲ Masquer debug technique' : '▼ Debug technique (sources SQL, metriques, traces)'}
              </Button>
              {debugVisible && (
                <div>
                  {payloadInvestigation && (
                    <React.Fragment>
                      <SectionContrat payload={payloadInvestigation} classes={classes} />
                      <SectionDebugSources payload={payloadInvestigation} resultats={resultats} classes={classes} />
                    </React.Fragment>
                  )}
                  {selection && <SectionResume items={selection.resumeNarratif} classes={classes} />}
                  {selection && <SectionAnomalies items={selection.anomaliesStagnations} classes={classes} />}
                  {selection && <SectionPreuves items={selection.preuves} classes={classes} />}
                  {selection && <SectionVoisinage valeur={selection.voisinageRelationnel} classes={classes} />}
                  <SectionTracesLocales traces={tracesLocales} classes={classes} />
                </div>
              )}
            </Paper>
          </Grid>
        )}

      </Grid>
    </Container>
  );
};

const SectionInvestigationCamera = ({ payload, contexte = {}, classes, indexSignauxDocumentaires, onReprojectionTerminee }) => {
  const [lignesOuvertes, setLignesOuvertes] = useState({});
  const [episodesOuverts, setEpisodesOuverts] = useState({});
  const [filtres, setFiltres] = useState({
    severite: '',
    evenement: '',
    source: '',
    domaine: '',
    texte: '',
    heureDebut: '',
    heureFin: '',
    mode: '',
  });
  const [snapshotOuvert, setSnapshotOuvert] = useState(null);
  const [chargementSnapshot, setChargementSnapshot] = useState(false);
  const [erreurSnapshot, setErreurSnapshot] = useState('');
  const [reprojectionEnCours, setReprojectionEnCours] = useState(false);
  const [rapportReprojection, setRapportReprojection] = useState(null);
  const [erreurReprojection, setErreurReprojection] = useState('');
  const [telechargement, setTelechargement] = useState(false);
  const [erreurTelechargement, setErreurTelechargement] = useState('');
  const focus = payload.focus || {};
  const contexteIncident = { ...(payload.contexteIncident || {}), ...(contexte || {}) };
  const snapshots = payload.snapshots || {};
  const syncSnapshots = snapshots.syncCamLog || [];
  const bdSnapshots = snapshots.bdLogFile || [];
  const snapshotsSources = syncSnapshots
    .concat(bdSnapshots)
    .sort((a, b) => timestampMs(a.recu_at) - timestampMs(b.recu_at));
  const timeline = payload.timelineRuntime || [];
  const optionsFiltres = useMemo(() => extraireOptionsFiltresTimeline(timeline), [timeline]);
  const timelineFiltree = useMemo(() => filtrerTimelineCamera(timeline, filtres, payload.journee), [timeline, filtres, payload.journee]);
  const episodesRuntime = useMemo(() => regrouperTimelineCamera(timelineFiltree, payload.camera), [timelineFiltree, payload.camera]);
  const episodeCibleId = useMemo(() => trouverEpisodeCible(episodesRuntime, contexteIncident), [episodesRuntime, contexteIncident.debut, contexteIncident.fin, contexteIncident.incident, contexteIncident.episode]);
  const signauxReconnus = useMemo(() => listerSignauxReconnus(
    indexSignauxDocumentaires,
    timeline.map((ligne) => ligne.event || ligne.evenement_type || ligne.contenu_brut),
  ), [indexSignauxDocumentaires, timeline]);
  const incidents = payload.incidents || [];
  const etatVideo = payload.etatVideo || {};
  const [signauxOuverts, setSignauxOuverts] = useState({});
  const refAnalyse = React.useRef(null);
  const refEpisodes = React.useRef(null);
  const basculer = (cle) => setLignesOuvertes((etat) => ({ ...etat, [cle]: !etat[cle] }));
  const basculerEpisode = (cle) => setEpisodesOuverts((etat) => ({ ...etat, [cle]: !etat[cle] }));
  const basculerSignal = (cle) => setSignauxOuverts((etat) => ({ ...etat, [cle]: !etat[cle] }));
  const ouvrirEpisodes = React.useCallback((episodeIds) => {
    const map = {};
    (episodeIds || []).forEach((id) => { map[id] = true; });
    setEpisodesOuverts((etat) => ({ ...etat, ...map }));
    if (refEpisodes.current) refEpisodes.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);
  const changerFiltre = (cle) => (event) => setFiltres((etat) => ({ ...etat, [cle]: event.target.value }));
  const appliquerMode = (mode) => setFiltres((etat) => ({ ...etat, mode: etat.mode === mode ? '' : mode }));
  const reinitialiserFiltres = () => {
    setFiltres({ severite: '', evenement: '', source: '', domaine: '', texte: '', heureDebut: '', heureFin: '', mode: '' });
    setEpisodesOuverts({});
    setLignesOuvertes({});
    setSignauxOuverts({});
  };
  useEffect(() => {
    const heures = heuresDepuisContexte(contexteIncident);
    if (!heures.heureDebut && !heures.heureFin) return;
    setFiltres((etat) => ({
      ...etat,
      heureDebut: heures.heureDebut || etat.heureDebut,
      heureFin: heures.heureFin || etat.heureFin,
    }));
  }, [contexteIncident.debut, contexteIncident.fin]);
  useEffect(() => {
    if (!episodeCibleId) return;
    setEpisodesOuverts((etat) => (etat[episodeCibleId] ? etat : { ...etat, [episodeCibleId]: true }));
  }, [episodeCibleId]);
  const lancerTelechargementBrut = () => {
    const camera = focus.telId || focus.camId || payload.camera;
    const journee = payload.journee;
    if (!camera || !journee) return;
    setTelechargement(true);
    setErreurTelechargement('');
    telechargerSyncCamLogJournee({ camera, journee })
      .then((blob) => {
        const urlBlob = URL.createObjectURL(blob);
        const lien = document.createElement('a');
        lien.href = urlBlob;
        lien.download = `synccamlog-${camera}-${journee}.txt`;
        document.body.appendChild(lien);
        lien.click();
        document.body.removeChild(lien);
        URL.revokeObjectURL(urlBlob);
        setTelechargement(false);
      })
      .catch((erreur) => {
        setErreurTelechargement(erreur.message);
        setTelechargement(false);
      });
  };

  const ouvrirSnapshot = (snapshot) => {
    if (!snapshot || !snapshot.id) return;
    const rapportMemorise = lireRapportReprojectionSnapshot(snapshot.id);
    setChargementSnapshot(true);
    setErreurSnapshot('');
    setSnapshotOuvert({
      ...snapshot,
      contenu_brut: snapshot.extrait || '',
    });
    setRapportReprojection(rapportMemorise);
    chargerSnapshotCamera({ id: snapshot.id })
      .then((snapshotComplet) => {
        setSnapshotOuvert(snapshotComplet || snapshot);
        setRapportReprojection(lireRapportReprojectionSnapshot(snapshot.id));
        setErreurReprojection('');
        setChargementSnapshot(false);
      })
      .catch((erreur) => {
        setErreurSnapshot(erreur.message);
        setChargementSnapshot(false);
      });
  };

  const fermerSnapshot = () => {
    const rafraichirApresFermeture = Boolean(rapportReprojection);
    setSnapshotOuvert(null);
    if (rafraichirApresFermeture && onReprojectionTerminee) onReprojectionTerminee();
  };

  const reprojeterSnapshotOuvert = () => {
    if (!snapshotOuvert || !snapshotOuvert.id) return;
    setReprojectionEnCours(true);
    setErreurReprojection('');
    setRapportReprojection(null);
    reprojeterSnapshotCamera({ id: snapshotOuvert.id })
      .then((rapport) => {
        memoriserRapportReprojectionSnapshot(snapshotOuvert.id, rapport);
        setRapportReprojection(rapport);
        setReprojectionEnCours(false);
        return chargerSnapshotCamera({ id: snapshotOuvert.id });
      })
      .then((snapshotComplet) => {
        if (snapshotComplet) setSnapshotOuvert(snapshotComplet);
      })
      .catch((erreur) => {
        setErreurReprojection(erreur.message);
        setReprojectionEnCours(false);
      });
  };

  return (
    <section>
      <div className={classes.header}>
        <div>
          <Typography variant="h5">Investigation camera {payload.camera}</Typography>
          <Typography variant="body2" className={classes.muted}>
            Journee {payload.journee || 'recente'} | contexte incident preserve
          </Typography>
        </div>
        <div>
          <Chip
            size="small"
            clickable
            label={`${episodesRuntime.length} episodes`}
            className={classes.chipNormal}
            style={{ marginRight: 6 }}
            onClick={() => refEpisodes.current && refEpisodes.current.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          />
          <Chip size="small" label={`${timelineFiltree.length}/${timeline.length} evenements runtime`} className={classes.chipUnknown} style={{ marginRight: 6 }} />
          <Chip
            size="small"
            clickable={signauxReconnus.length > 0}
            label={`${signauxReconnus.length} signaux documentes`}
            className={signauxReconnus.length ? classes.chipWatch : classes.chipUnknown}
            style={{ marginRight: 6 }}
            onClick={signauxReconnus.length > 0 ? () => refAnalyse.current && refAnalyse.current.scrollIntoView({ behavior: 'smooth', block: 'start' }) : undefined}
          />
          <Chip size="small" label={`${syncSnapshots.length} snapshots SyncCamLog`} className={classes.chipUnknown} style={{ marginRight: 6 }} />
          <Chip size="small" label={`${bdSnapshots.length} snapshots BDLogFile`} className={classes.chipUnknown} />
        </div>
      </div>

      {contexteIncident && (contexteIncident.debut || contexteIncident.fin || contexteIncident.event || contexteIncident.incident) && (
        <div className={`${classes.callout} ${classes.section}`}>
          <Typography variant="subtitle2">Contexte de depart conserve</Typography>
          <Typography variant="body2">
            {[
              contexteIncident.event ? `signal ${contexteIncident.event}` : '',
              contexteIncident.incident ? `ligne ${contexteIncident.incident}` : '',
              contexteIncident.debut || contexteIncident.fin ? `fenetre ${formaterHeure(contexteIncident.debut)} - ${formaterHeure(contexteIncident.fin)}` : '',
            ].filter(Boolean).join(' | ')}
          </Typography>
        </div>
      )}

      {signauxReconnus.length > 0 && (
        <div className={`${classes.callout} ${classes.section}`}>
          <Typography variant="subtitle2">Signaux documentaires reconnus</Typography>
          <Typography variant="body2">
            {signauxReconnus.map((signal) => signal.nom).join(', ')}
          </Typography>
        </div>
      )}

      <div ref={refAnalyse}>
        <SectionAnalyse
          signauxReconnus={signauxReconnus}
          timeline={timeline}
          episodesRuntime={episodesRuntime}
          classes={classes}
          ouvrirEpisodes={ouvrirEpisodes}
        />
      </div>

      <div className={`${classes.focusCamera} ${classes.section}`}>
        <Grid container spacing={2}>
          <Grid item xs={6} md={2}><ChampFocus libelle="Camera" valeur={focus.camId || payload.camera} /></Grid>
          <Grid item xs={6} md={2}><ChampFocus libelle="TelId" valeur={focus.telId || payload.camera} /></Grid>
          <Grid item xs={6} md={2}><ChampFocus libelle="Premier evenement" valeur={formaterDate(focus.premierEvenementAt)} /></Grid>
          <Grid item xs={6} md={2}><ChampFocus libelle="Dernier evenement" valeur={formaterDate(focus.dernierEvenementAt)} /></Grid>
          <Grid item xs={6} md={2}><ChampFocus libelle="Incidents" valeur={focus.nbIncidents || incidents.length} /></Grid>
          <Grid item xs={6} md={2}><ChampFocus libelle="Contrat" valeur={payload.versionContrat} /></Grid>
        </Grid>
      </div>

      <div className={classes.section} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button
          variant="outlined"
          size="small"
          disabled={telechargement || !payload.journee || !(focus.telId || focus.camId || payload.camera)}
          onClick={lancerTelechargementBrut}
        >
          {telechargement ? 'Téléchargement...' : 'Télécharger synccamlog brut journée'}
        </Button>
        {erreurTelechargement && (
          <Typography variant="caption" style={{ color: '#dc2626' }}>
            Erreur: {erreurTelechargement}
          </Typography>
        )}
      </div>

      {payload.avertissements && payload.avertissements.length > 0 && (
        <div className={`${classes.warning} ${classes.section}`}>
          <Typography variant="body2">Avertissements backend: {payload.avertissements.join(', ')}</Typography>
        </div>
      )}

      <Grid container spacing={2} className={classes.section}>
        <Grid item xs={12} md={7}>
          <div ref={refEpisodes}>
            <Typography variant="h6">Episodes runtime SyncCamLog</Typography>
          </div>
          <Typography variant="body2" className={classes.muted}>
            Regroupement local par camera, proximite temporelle et famille logique. Le brut reste ouvrable dans chaque episode.
          </Typography>
          <FiltresTimelineCamera
            filtres={filtres}
            options={optionsFiltres}
            changerFiltre={changerFiltre}
            appliquerMode={appliquerMode}
            reinitialiserFiltres={reinitialiserFiltres}
            classes={classes}
          />
          <SectionSnapshotsSyncCam
            snapshots={syncSnapshots}
            ouvrirSnapshot={ouvrirSnapshot}
            classes={classes}
          />
          {timelineFiltree.length > 0 ? (
            <div className={classes.section}>
              {episodesRuntime.slice(0, 80).map((episode) => (
                <EpisodeRuntime
                  key={episode.id}
                  episode={episode}
                  selectionne={episode.id === episodeCibleId}
                  ouvert={Boolean(episodesOuverts[episode.id])}
                  signauxOuverts={signauxOuverts}
                  lignesOuvertes={lignesOuvertes}
                  basculerEpisode={basculerEpisode}
                  basculerSignal={basculerSignal}
                  basculerLigne={basculer}
                  classes={classes}
                  indexSignauxDocumentaires={indexSignauxDocumentaires}
                />
              ))}
              {episodesRuntime.length > 80 && (
                <Typography variant="body2" className={classes.muted}>+{episodesRuntime.length - 80} episodes supplementaires non affiches par defaut.</Typography>
              )}
              <details className={classes.section}>
                <summary>Brut complet filtre ({timelineFiltree.length} lignes)</summary>
                <pre className={classes.preClair}>{timelineFiltree.map(ligneBruteRuntime).join('\n')}</pre>
              </details>
            </div>
          ) : (
            <Typography className={classes.muted}>Aucune ligne SyncCamLog ne correspond aux filtres courants.</Typography>
          )}
        </Grid>

        <Grid item xs={12} md={5}>
          <Typography variant="h6">Etat video BDLogFile</Typography>
          <Typography variant="body2" className={classes.muted}>
            Projection separee: RAW, videos, pending, convergence et decoupes.
          </Typography>
          <SectionBdLogOperationnel etatVideo={etatVideo} classes={classes} />
          <Table size="small" className={classes.section}>
            <TableHead>
              <TableRow>
                <TableCell>Reception</TableCell>
                <TableCell>Lignes</TableCell>
                <TableCell>Taille</TableCell>
                <TableCell>Projection</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(etatVideo.snapshots || []).slice(-8).reverse().map((snapshot) => (
                <TableRow key={snapshot.id || snapshot.hash_fichier} hover className={classes.rowClickable} onClick={() => ouvrirSnapshot(snapshot)}>
                  <TableCell>{formaterDate(snapshot.recu_at)}</TableCell>
                  <TableCell>{nombre(snapshot.nb_lignes)} lignes</TableCell>
                  <TableCell>{nombre(snapshot.taille_octets)} octets</TableCell>
                  <TableCell>
                    {nombre(snapshot.resume && snapshot.resume.nbRawRecents)} RAW /
                    {' '}{nombre(snapshot.resume && snapshot.resume.nbVideosDecoupees)} videos /
                    {' '}{nombre(snapshot.resume && snapshot.resume.nbPending202)} pending
                  </TableCell>
                </TableRow>
              ))}
              {(!etatVideo.snapshots || etatVideo.snapshots.length === 0) && (
                <TableRow><TableCell colSpan={4}>Aucun snapshot BDLogFile expose.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>

          <Typography variant="h6" className={classes.section}>Incidents camera</Typography>
          <Table size="small">
            <TableBody>
              {incidents.slice(0, 12).map((incident) => (
                <IncidentCameraLigne
                  key={incident.ligne_id || incident.id}
                  incident={incident}
                  classes={classes}
                  indexSignauxDocumentaires={indexSignauxDocumentaires}
                />
              ))}
              {incidents.length === 0 && (
                <TableRow><TableCell>Aucun incident recent pour ce focus.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Grid>
      </Grid>

      <Typography variant="h6" className={classes.section}>Snapshots sources</Typography>
      <SectionDebugSnapshotsSources debug={payload.debugSnapshotsSources} classes={classes} />
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Type</TableCell>
            <TableCell>Reception</TableCell>
            <TableCell>Fichier</TableCell>
            <TableCell>Lignes</TableCell>
            <TableCell>Taille</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {snapshotsSources.slice().reverse().map((snapshot) => (
            <TableRow key={snapshot.id || snapshot.hash_fichier} hover className={classes.rowClickable} onClick={() => ouvrirSnapshot(snapshot)}>
              <TableCell>{/bdlogfile/i.test(snapshot.nom_fichier || snapshot.remote_path) ? 'BDLogFile' : 'SyncCamLog'}</TableCell>
              <TableCell>{formaterDate(snapshot.recu_at)}</TableCell>
              <TableCell style={{ fontFamily: 'monospace', fontSize: 11 }}>{snapshot.remote_path}</TableCell>
              <TableCell>{nombre(snapshot.nb_lignes)}</TableCell>
              <TableCell>{nombre(snapshot.taille_octets)}</TableCell>
            </TableRow>
          ))}
          {snapshotsSources.length === 0 && (
            <TableRow><TableCell colSpan={5}>Aucun snapshot source expose.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
      <Dialog open={Boolean(snapshotOuvert)} onClose={fermerSnapshot} maxWidth="lg" fullWidth>
        <DialogTitle>{snapshotOuvert ? texte(snapshotOuvert.nom_fichier || snapshotOuvert.remote_path) : 'Snapshot'}</DialogTitle>
        <DialogContent>
          {snapshotOuvert && (
            <div>
              <Typography variant="body2" className={classes.muted}>
                {formaterDate(snapshotOuvert.recu_at)} | {nombre(snapshotOuvert.nb_lignes)} lignes | {nombre(snapshotOuvert.taille_octets)} octets
              </Typography>
              <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: 12, marginTop: 6, wordBreak: 'break-word' }}>
                {texte(snapshotOuvert.remote_path)}
              </Typography>
              {erreurSnapshot && (
                <div className={`${classes.warning} ${classes.section}`}>
                  <Typography variant="body2">Contenu complet indisponible: {erreurSnapshot}</Typography>
                  <Typography variant="body2">Extrait disponible ci-dessous.</Typography>
                </div>
              )}
              {chargementSnapshot && <Typography className={classes.section}>Chargement du contenu brut...</Typography>}
              {rapportReprojection && (
                <div className={`${classes.callout} ${classes.section}`}>
                  <Typography variant="subtitle2">Rapport de reprojection</Typography>
                  <Typography variant="body2">
                    {rapportReprojection.lignesLues || 0} lues | {rapportReprojection.lignesMisesAJour || 0} mises a jour | {rapportReprojection.lignesAjoutees || 0} ajoutees | {rapportReprojection.lignesInchangees || 0} inchangees
                  </Typography>
                  <Typography variant="body2" className={classes.muted}>
                    Diagnostics session affectes: {(rapportReprojection.diagnosticsSessionsAffectes || []).length || 0}
                  </Typography>
                  <RapportExecutionReprojection rapport={rapportReprojection} classes={classes} />
                </div>
              )}
              {erreurReprojection && (
                <div className={`${classes.warning} ${classes.section}`}>
                  <Typography variant="body2">Reprojection impossible: {erreurReprojection}</Typography>
                </div>
              )}
              <pre className={classes.preClair}>{texte(snapshotOuvert.contenu_brut || snapshotOuvert.extrait)}</pre>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={reprojeterSnapshotOuvert}
            color="primary"
            variant="outlined"
            disabled={reprojectionEnCours || chargementSnapshot || !snapshotOuvert || !snapshotOuvert.id}
          >
            {reprojectionEnCours ? 'Reprojection...' : 'Reprojeter ce fichier'}
          </Button>
          <Button onClick={fermerSnapshot} color="primary">Fermer</Button>
        </DialogActions>
      </Dialog>
    </section>
  );
};

const RapportExecutionReprojection = ({ rapport, classes }) => {
  const execution = rapport && rapport.rapportExecution;
  if (!execution) {
    return (
      <div className={`${classes.warning} ${classes.section}`}>
        <Typography variant="body2">
          Rapport incomplet: le backend appele par le bouton ne retourne pas les preuves de reprojection AV_SYNC_AUDIT.
        </Typography>
      </div>
    );
  }
  const lignesModifiees = execution.lignesModifiees || [];
  const exempleAv = execution.exemplePayloadAvSyncAudit;
  const diagnostics = execution.diagnosticsSession || {};
  const av = execution.avSyncAudit || null;
  const erreursParType = execution.erreursParType || {};
  const exempleVerifie = av && av.exempleVerifieApresCommit ? av.exempleVerifieApresCommit : exempleAv;

  return (
    <div className={classes.section}>
      {!av && (
        <div className={`${classes.warning} ${classes.section}`}>
          <Typography variant="body2">
            Rapport backend obsolète: aucune section AV_SYNC_AUDIT structuree n'a ete retournee. Cette reprojection ne prouve pas l'enrichissement des huit audits.
          </Typography>
        </div>
      )}
      <Typography variant="subtitle2" className={classes.section}>AV_SYNC_AUDIT</Typography>
      <Typography variant="body2">
        {nombre((av && av.evenementsTrouves) || 0)} evenements trouves | {' '}
        {nombre((av && av.lignesExistantesRetrouvees) || 0)} lignes existantes retrouvees | {' '}
        {nombre((av && av.payloadsEnrichis) || 0)} payloads enrichis | {' '}
        {nombre((av && av.dejaConformes) || 0)} deja conformes | {' '}
        {nombre((av && av.conflitsHorsFichier) || 0)} conflits hors fichier | {' '}
        {nombre((av && av.echecs) || 0)} echecs
      </Typography>
      {exempleVerifie ? (
        <pre className={classes.preClair}>{JSON.stringify({
          numeroLigneBrute: exempleVerifie.numeroLigneBrute || exempleVerifie.numeroLigne,
          monitoringLogLigneId: exempleVerifie.monitoringLogLigneId || exempleVerifie.idLigne,
          hashLigne: exempleVerifie.hashLigneCalcule || exempleVerifie.hashLigne,
          rawId: exempleVerifie.payloadParse && exempleVerifie.payloadParse.rawId,
          driftUs: exempleVerifie.payloadParse && exempleVerifie.payloadParse.driftUs,
          driftSec: exempleVerifie.payloadParse && exempleVerifie.payloadParse.driftSec,
          hasVideo: exempleVerifie.payloadParse && exempleVerifie.payloadParse.hasVideo,
          hasAudio: exempleVerifie.payloadParse && exempleVerifie.payloadParse.hasAudio,
          updateRowCount: exempleVerifie.updateRowCount,
          payloadReluApresCommit: exempleVerifie.payloadReluApresCommit,
        }, null, 2)}</pre>
      ) : (
        <Typography variant="body2" className={classes.muted}>Aucun AV_SYNC_AUDIT verifie apres commit.</Typography>
      )}
      {Object.keys(erreursParType).length > 0 && (
        <Typography variant="body2" className={classes.muted}>
          Erreurs regroupees: {texte(erreursParType)}
        </Typography>
      )}
      <Typography variant="body2">
        Diagnostics session invalides: {diagnostics.diagnosticsInvalides ? 'oui' : 'non'} | {' '}
        reconstruits pendant la reprojection: {diagnostics.diagnosticsReconstruitsPendantReprojection ? 'oui' : 'non'} | {' '}
        {diagnostics.donneesSourcesCockpitRafraichies ? 'donnees sources cockpit rafraichies' : 'donnees sources cockpit inchangees'}
      </Typography>
      <Typography variant="body2" className={classes.muted}>
        Reconstruction: {texte(diagnostics.reconstructionCockpit)}
      </Typography>
      <Typography variant="subtitle2" className={classes.section}>Lignes effectivement modifiees</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Ligne</TableCell>
            <TableCell>Action</TableCell>
            <TableCell>Evenement</TableCell>
            <TableCell>Champs</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lignesModifiees.slice(0, 20).map((ligne) => (
            <TableRow key={`${ligne.action}-${ligne.idLigne || ligne.numeroLigne}`}>
              <TableCell>{ligne.numeroLigne}</TableCell>
              <TableCell>{ligne.action}</TableCell>
              <TableCell>{texte(ligne.evenementTypeAvant)} -> {texte(ligne.evenementTypeApres)}</TableCell>
              <TableCell>{texte([...(ligne.champsModifies || []), ...(ligne.payloadChampsModifies || []).map((champ) => `payload.${champ}`)])}</TableCell>
            </TableRow>
          ))}
          {lignesModifiees.length === 0 && (
            <TableRow><TableCell colSpan={4}>Aucune ligne modifiee par cette reprojection.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
      {lignesModifiees.length > 20 && (
        <Typography variant="body2" className={classes.muted}>{lignesModifiees.length - 20} lignes supplementaires masquees.</Typography>
      )}
      <Typography variant="subtitle2" className={classes.section}>Exemple AV_SYNC_AUDIT avant/apres</Typography>
      {exempleAv ? (
        <pre className={classes.preClair}>{JSON.stringify({
          ligne: exempleAv.numeroLigne,
          action: exempleAv.action,
          payloadAvant: exempleAv.avant && exempleAv.avant.payload,
          payloadApres: exempleAv.apres && exempleAv.apres.payload,
          payloadChampsModifies: exempleAv.payloadChampsModifies,
        }, null, 2)}</pre>
      ) : (
        <Typography variant="body2" className={classes.muted}>Aucun AV_SYNC_AUDIT modifie dans ce passage.</Typography>
      )}
    </div>
  );
};

const FiltresTimelineCamera = ({ filtres, options, changerFiltre, appliquerMode, reinitialiserFiltres, classes }) => (
  <div>
    <div className={classes.filtresInvestigation}>
      <FiltreSelect libelle="Severite" valeur={filtres.severite} options={options.severites} onChange={changerFiltre('severite')} />
      <FiltreSelect libelle="Evenement" valeur={filtres.evenement} options={options.evenements} onChange={changerFiltre('evenement')} />
      <FiltreSelect libelle="Source" valeur={filtres.source} options={options.sources} onChange={changerFiltre('source')} />
      <FiltreSelect libelle="Domaine" valeur={filtres.domaine} options={options.domaines} onChange={changerFiltre('domaine')} />
      <TextField label="Texte brut" variant="outlined" size="small" value={filtres.texte} onChange={changerFiltre('texte')} />
      <TextField
        label="Heure debut"
        type="time"
        variant="outlined"
        size="small"
        value={filtres.heureDebut}
        onChange={changerFiltre('heureDebut')}
        InputLabelProps={{ shrink: true }}
        inputProps={{ step: 300 }}
      />
      <TextField
        label="Heure fin"
        type="time"
        variant="outlined"
        size="small"
        value={filtres.heureFin}
        onChange={changerFiltre('heureFin')}
        InputLabelProps={{ shrink: true }}
        inputProps={{ step: 300 }}
      />
    </div>
    <div className={classes.barreActions}>
      {['critique', 'convergence', 'recovery', 'erreur'].map((mode) => (
        <Button
          key={mode}
          size="small"
          variant={filtres.mode === mode ? 'contained' : 'outlined'}
          color={filtres.mode === mode ? 'primary' : 'default'}
          onClick={() => appliquerMode(mode)}
        >
          {libelleModeFiltre(mode)}
        </Button>
      ))}
      <Button size="small" variant="text" onClick={reinitialiserFiltres}>Reinitialiser</Button>
    </div>
  </div>
);

const FiltreSelect = ({ libelle, valeur, options, onChange }) => (
  <TextField
    select
    SelectProps={{ native: true }}
    label={libelle}
    variant="outlined"
    size="small"
    value={valeur}
    onChange={onChange}
  >
    <option value="">Tous</option>
    {(options || []).map((option) => (
      <option key={option} value={option}>{option}</option>
    ))}
  </TextField>
);

const SectionSnapshotsSyncCam = ({ snapshots, ouvrirSnapshot, classes }) => (
  <details className={classes.section}>
    <summary>Ouvrir SyncCamLog complet ({snapshots.length} snapshots)</summary>
    <Table size="small" className={classes.tableMini}>
      <TableHead>
        <TableRow>
          <TableCell>Reception</TableCell>
          <TableCell>Lignes</TableCell>
          <TableCell>Taille</TableCell>
          <TableCell>Fichier</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {(snapshots || []).slice(-12).reverse().map((snapshot) => (
          <TableRow key={snapshot.id || snapshot.hash_fichier} hover className={classes.rowClickable} onClick={() => ouvrirSnapshot(snapshot)}>
            <TableCell>{formaterDate(snapshot.recu_at)}</TableCell>
            <TableCell>{nombre(snapshot.nb_lignes)}</TableCell>
            <TableCell>{nombre(snapshot.taille_octets)} octets</TableCell>
            <TableCell style={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-word' }}>{snapshot.remote_path}</TableCell>
          </TableRow>
        ))}
        {(!snapshots || snapshots.length === 0) && (
          <TableRow><TableCell colSpan={4}>Aucun SyncCamLog complet expose.</TableCell></TableRow>
        )}
      </TableBody>
    </Table>
  </details>
);

const SectionDebugSnapshotsSources = ({ debug, classes }) => {
  if (!debug) return null;
  const parType = debug.parType || {};
  const types = ['SyncCamLog', 'BDLogFile', 'Autre'].filter((type) => parType[type]);

  return (
    <div className={`${classes.callout} ${classes.section}`}>
      <Typography variant="subtitle2">Debug temporaire snapshots sources</Typography>
      <Typography variant="body2" className={classes.muted}>
        Filtre camera {texte(debug.filtre && debug.filtre.camera)} | journee {texte(debug.filtre && debug.filtre.journee)} | limite SQL fichiers {texte(debug.limiteAppliquee && debug.limiteAppliquee.requeteFichiers)} | UI {texte(debug.limiteAppliquee && debug.limiteAppliquee.sectionUi)}
      </Typography>
      <Table size="small" className={classes.tableMini}>
        <TableHead>
          <TableRow>
            <TableCell>Type</TableCell>
            <TableCell>Total base</TableCell>
            <TableCell>Retournes API</TableCell>
            <TableCell>Min reception</TableCell>
            <TableCell>Max reception</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {types.map((type) => {
            const stats = parType[type] || {};
            return (
              <TableRow key={type}>
                <TableCell>{type}</TableCell>
                <TableCell>{nombre(stats.totalBase)}</TableCell>
                <TableCell>{nombre(stats.retournes)}</TableCell>
                <TableCell>{formaterDate(stats.minRecuAt)}</TableCell>
                <TableCell>{formaterDate(stats.maxRecuAt)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

const filtrerNonDocumente = (items) => (
  (items || []).filter((item) => !/non documente/i.test(String(item || '')))
);

const detecterBouclesEtFlux = (signauxPresents) => {
  const boucles = {};
  const flux = {};
  (signauxPresents || []).forEach((signal) => {
    filtrerNonDocumente(signal.boucles_associees || []).forEach((b) => {
      const cle = String(b || '').trim();
      if (!cle) return;
      if (!boucles[cle]) boucles[cle] = [];
      boucles[cle].push(signal.nom);
    });
    (signal.flux_affectes || []).forEach((f) => {
      const cle = String(f || '').trim();
      if (!cle) return;
      if (!flux[cle]) flux[cle] = [];
      flux[cle].push(signal.nom);
    });
  });
  return { boucles, flux };
};

const PatternBrutToggle = ({ extrait }) => {
  const [visible, setVisible] = React.useState(false);
  const texteRaw = texte(extrait);
  if (!texteRaw) return null;
  return (
    <span style={{ fontSize: 11 }}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setVisible((v) => !v); }}
        style={{ fontSize: 11, cursor: 'pointer', color: '#2563eb', background: 'none', border: 'none', padding: 0, marginRight: 6 }}
      >
        {visible ? 'Masquer brut' : 'Voir brut'}
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); if (navigator.clipboard) navigator.clipboard.writeText(texteRaw); }}
        style={{ fontSize: 11, cursor: 'pointer', color: '#6b7280', background: 'none', border: 'none', padding: 0 }}
      >
        Copier brut
      </button>
      {visible && (
        <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 11, color: '#374151', margin: '4px 0 0 0', padding: '4px 6px', backgroundColor: '#f3f4f6', borderRadius: 3 }}>
          {texteRaw}
        </pre>
      )}
    </span>
  );
};

const EpisodeRuntime = ({ episode, selectionne, ouvert, signauxOuverts, lignesOuvertes, basculerEpisode, basculerSignal, basculerLigne, classes, indexSignauxDocumentaires }) => {
  const temporalite = useMemo(() => decrireTemporaliteEpisode(episode), [episode]);
  const signauxParPattern = useMemo(
    () => episode.patterns.map((compact) =>
      trouverSignalDansTexte(indexSignauxDocumentaires, `${compact.evenement} ${compact.extrait}`)
    ),
    [episode.patterns, indexSignauxDocumentaires]
  );
  const signauxPresents = signauxParPattern.filter(Boolean);
  const nbSignaux = signauxPresents.length;
  const signalPrincipal = signauxParPattern.find((s) => s && (s.criticite === 'critique' || s.criticite === 'rouge'))
    || signauxPresents[0]
    || null;
  const { boucles, flux } = useMemo(() => detecterBouclesEtFlux(signauxPresents), [signauxPresents]);
  const nbRecoveryStart = useMemo(
    () => (episode.evenements || []).filter((l) => {
      const ev = String(l.event || l.evenement_type || '').toUpperCase();
      return ev.includes('RECOVERY_START') || ev.includes('CAMERA_RECOVERY_START');
    }).length,
    [episode.evenements]
  );
  const hasLoopGuard = useMemo(
    () => (episode.evenements || []).some((l) => {
      const ev = String(l.event || l.evenement_type || '').toUpperCase();
      return ev.includes('LOOP_GUARD') || ev.includes('RECOVERY_LOOP_GUARD');
    }),
    [episode.evenements]
  );
  const bouclesDetectees = useMemo(
    () => Object.entries(boucles).filter(([, signaux]) => signaux.length >= 3).sort((a, b) => b[1].length - a[1].length),
    [boucles]
  );
  const fluxPrincipaux = useMemo(
    () => Object.entries(flux).sort((a, b) => b[1].length - a[1].length).slice(0, 2),
    [flux]
  );

  return (
    <div className={`${classes.episode} ${selectionne ? classes.episodeSelectionne : ''}`}>
      {/* Niveau 1 : resume compact toujours visible */}
      <div className={classes.episodeHeader} onClick={() => basculerEpisode(episode.id)} role="button" tabIndex={0}>
        <div>
          <div className={classes.episodeTitre}>
            <strong className={classes.episodeTemporalite}>
              {temporalite.lignesTitre.map((ligne) => <span key={ligne}>{ligne}</span>)}
            </strong>
            <Chip size="small" label={episode.famillePrincipale} className={classes.chipUnknown} />
            <Chip size="small" label={episode.severite} className={classeStagnation(classes, severitePourClasse(episode.severite))} />
            {selectionne && <Chip size="small" label="cible" className={classes.chipNormal} />}
          </div>
          <div className={classes.episodeIndicateurTemporel}>{temporalite.indicateur}</div>
          <div className={classes.episodeMeta}>
            <span>{episode.nbEvenements} evenements</span>
            {nbSignaux > 0 && (
              <span>{nbSignaux} signal{nbSignaux > 1 ? 'x' : ''} documente{nbSignaux > 1 ? 's' : ''}</span>
            )}
            {signalPrincipal && (
              <span>Signal principal : <strong>{signalPrincipal.nom}</strong></span>
            )}
          </div>
          {(bouclesDetectees.length > 0 || fluxPrincipaux.length > 0) && (
            <div style={{ fontSize: 12, marginTop: 3 }}>
              {bouclesDetectees.slice(0, 2).map(([b]) => (
                <span key={b} style={{ color: '#15803d', marginRight: 10 }}>⟳ {b}</span>
              ))}
              {fluxPrincipaux.slice(0, 2).map(([f]) => (
                <span key={f} style={{ color: '#1d4ed8', marginRight: 10 }}>↗ {f}</span>
              ))}
            </div>
          )}
        </div>
        <Button
          size="small"
          variant="outlined"
          onClick={(event) => { event.stopPropagation(); basculerEpisode(episode.id); }}
        >
          {ouvert ? 'Masquer' : 'Ouvrir'}
        </Button>
      </div>

      {/* Niveau 2 : liste des signaux/patterns (episode ouvert) */}
      {ouvert && (
        <div className={classes.repetitions}>
          {/* Boucle confirmée par LOOP_GUARD */}
          {hasLoopGuard && (
            <div className={classes.boucleDetectee} style={{ borderColor: '#dc2626', backgroundColor: '#fef2f2' }}>
              <div className={classes.boucleDetecteeTitre} style={{ color: '#dc2626' }}>BOUCLE CONFIRMEE — Recovery camera</div>
              <div style={{ fontSize: 12, color: '#dc2626' }}>
                LOOP_GUARD déclenché — boucle de recovery confirmée par le système
              </div>
            </div>
          )}
          {/* Boucles détectées par convergence de signaux (seuil élevé) */}
          {!hasLoopGuard && bouclesDetectees.map(([boucle, signaux]) => (
            <div key={boucle} className={classes.boucleDetectee}>
              <div className={classes.boucleDetecteeTitre}>BOUCLE POSSIBLE — {boucle}</div>
              <div style={{ fontSize: 12, color: '#15803d' }}>
                Confiance élevée — {signaux.length} signaux convergent
              </div>
              <div style={{ fontSize: 12, marginTop: 2 }}>
                {signaux.map((nom) => <span key={nom} style={{ marginRight: 8 }}>✓ {nom}</span>)}
              </div>
            </div>
          ))}
          {/* Restart contrôlé (recovery normale, pas une boucle) */}
          {!hasLoopGuard && !bouclesDetectees.length && nbRecoveryStart >= 1 && (
            <div style={{ backgroundColor: '#f9fafb', border: '1px solid #d1d5db', borderRadius: 4, padding: '6px 10px', marginBottom: 6 }}>
              <div style={{ fontWeight: 600, color: '#374151', marginBottom: 2, fontSize: 13 }}>
                Restart contrôlé ×{nbRecoveryStart}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                Recovery autonome — vérifier CAMERA_RECOVERY_RESULT pour confirmer la reprise
              </div>
            </div>
          )}
          {/* Flux actifs dans cet épisode */}
          {fluxPrincipaux.map(([fluxNom, signaux]) => (
            <div key={fluxNom} className={classes.fluxImpacte}>
              <div className={classes.fluxImpacteTitre}>FLUX ACTIF — {fluxNom}</div>
              <div style={{ fontSize: 12, color: '#1d4ed8' }}>
                {signaux.length} signal{signaux.length > 1 ? 'x' : ''} : {signaux.join(', ')}
              </div>
            </div>
          ))}
          {episode.patterns.map((compact, index) => {
            const signal = signauxParPattern[index];
            const cleSignal = `${episode.id}-${index}`;
            const signalOuvert = Boolean(signauxOuverts[cleSignal]);
            return (
              <React.Fragment key={compact.id}>
                <div
                  className={classes.ligneCompacte}
                  onClick={() => signal && basculerSignal(cleSignal)}
                  role={signal ? 'button' : undefined}
                  tabIndex={signal ? 0 : undefined}
                  style={signal ? { cursor: 'pointer' } : {}}
                >
                  <span>
                    <Chip size="small" label={`x${compact.nb}`} className={compact.nb > 1 ? classes.chipWatch : classes.chipUnknown} />
                  </span>
                  <span className={classes.texteCompact} style={{ fontWeight: 700 }}>
                    {texte(compact.evenement)}
                    {signal && (
                      <Chip
                        size="small"
                        label={texte(signal.criticite)}
                        className={classeCriticiteSignal(classes, signal.criticite)}
                        style={{ marginLeft: 4, verticalAlign: 'middle' }}
                      />
                    )}
                  </span>
                  <span className={classes.texteCompact}>
                    <PatternBrutToggle extrait={compact.extrait} />
                  </span>
                  <span className={classes.muted}>
                    {formaterHeure(compact.premierTimestamp)}–{formaterHeure(compact.dernierTimestamp)}
                    {signal && !signalOuvert && <span style={{ marginLeft: 6, color: '#2563eb' }}>▸ voir signal</span>}
                  </span>
                </div>
                {/* Niveau 3 : documentation du signal (signal ouvert) */}
                {signal && signalOuvert && (
                  <FicheSignalDocumentaire signal={signal} classes={classes} compacte={false} />
                )}
              </React.Fragment>
            );
          })}

          {/* Niveau 4 : lignes brutes (explicitement derriere un details) */}
          <details className={classes.section}>
            <summary>Voir brut ({episode.nbEvenements} lignes)</summary>
            <div className={classes.zoneBrute}>
              {episode.evenements.slice(0, 180).map((ligne, index) => {
                const cle = cleLigneRuntime(ligne, index);
                const ouverte = lignesOuvertes[cle];
                return (
                  <div key={cle}>
                    <div className={classes.timelineCamera} onClick={() => basculerLigne(cle)} role="button" tabIndex={0}>
                      <span className={classes.timelineCell} style={{ fontFamily: 'monospace', color: '#4b5563' }}>{formaterDate(ligne.timestamp)}</span>
                      <span className={classes.timelineCell}>
                        <Chip size="small" label={texte(ligne.niveau)} className={classeStagnation(classes, ligne.niveau === 'ERROR' ? 'critique' : 'normal')} />
                      </span>
                      <span className={classes.timelineCell} style={{ fontWeight: 700 }}>{texte(ligne.event || ligne.evenement_type)}</span>
                      <span className={classes.timelineCell}>{[ligne.source, ligne.domaine].filter(Boolean).join(' / ') || 'source n/d'}</span>
                      <span className={classes.timelineCell}>{ouverte ? texte(ligne.contenu_brut) : tronquer(texte(ligne.contenu_brut), 140)}</span>
                    </div>
                    {ouverte && (
                      <React.Fragment>
                        <details className={classes.section}>
                          <summary>Projection canonique</summary>
                          <pre className={classes.preClair}>{JSON.stringify(projectionCanoniqueRuntime(ligne), null, 2)}</pre>
                        </details>
                        <pre className={classes.preClair}>{texte(ligneBruteRuntime(ligne))}</pre>
                      </React.Fragment>
                    )}
                  </div>
                );
              })}
            </div>
            {episode.evenements.length > 180 && (
              <Typography variant="body2" className={classes.muted}>
                +{episode.evenements.length - 180} lignes brutes supplementaires dans le SyncCamLog complet.
              </Typography>
            )}
          </details>
        </div>
      )}
    </div>
  );
};

const IncidentCameraLigne = ({ incident, classes, indexSignauxDocumentaires }) => {
  const signal = trouverSignalDansTexte(indexSignauxDocumentaires, `${incident.evenement_type} ${incident.contenu_brut || ''}`);

  return (
    <React.Fragment>
      <TableRow>
        <TableCell>{formaterDate(incident.ts_event || incident.recu_at)}</TableCell>
        <TableCell>{texte(incident.evenement_type)}</TableCell>
        <TableCell>{texte(incident.severite_operationnelle || incident.niveau)}</TableCell>
      </TableRow>
      {signal && (
        <TableRow>
          <TableCell colSpan={3}>
            <FicheSignalDocumentaire signal={signal} classes={classes} compacte />
          </TableCell>
        </TableRow>
      )}
      <TableRow>
        <TableCell colSpan={3}>
          <details>
            <summary>Projection canonique</summary>
            <pre className={classes.preClair}>{JSON.stringify(projectionCanoniqueRuntime(incident), null, 2)}</pre>
          </details>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

const FicheSignalDocumentaire = ({ signal, classes, compacte = false }) => {
  if (!signal) return null;

  return (
    <div className={classes.signalDocumentaire}>
      <div className={classes.signalDocumentaireTitre}>
        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>{signal.nom}</Typography>
        <Chip size="small" label={`criticite ${texte(signal.criticite)}`} className={classeCriticiteSignal(classes, signal.criticite)} />
        <Chip size="small" label={`confiance ${texte(signal.confiance)}`} className={classeConfiance(classes, signal.confiance)} />
      </div>
      <Typography variant="body2">{texte(signal.description)}</Typography>
      {!compacte && (
        <div className={classes.grilleSignalLarge}>
          <BlocSignalDocumentaire titre="Symptomes observes" items={signal.symptomes_observes} classes={classes} />
          <BlocSignalDocumentaire titre="Preuves observables" items={signal.preuves} classes={classes} />
          <BlocSignalDocumentaire titre="Boucles associees" items={filtrerNonDocumente(signal.boucles_associees)} classes={classes} />
          <BlocSignalDocumentaire titre="Flux affectes" items={signal.flux_affectes} classes={classes} />
          <BlocSignalDocumentaire titre="Actions recommandees" items={signal.actions_recommandees} classes={classes} />
          <BlocSignalDocumentaire titre="Ne prouve pas" items={signal.ce_que_le_signal_ne_prouve_pas} classes={classes} />
        </div>
      )}
      {compacte && signal.actions_recommandees && signal.actions_recommandees.length > 0 && (
        <Typography variant="body2" className={classes.section}>
          <strong>Action:</strong> {signal.actions_recommandees[0]}
        </Typography>
      )}
    </div>
  );
};

const BlocSignalDocumentaire = ({ titre, items, classes }) => (
  <div>
    <Typography variant="caption" className={classes.muted}>{titre}</Typography>
    {items && items.length > 0 ? (
      <ul className={classes.denseList}>
        {items.map((item, index) => <li key={index}>{item}</li>)}
      </ul>
    ) : (
      <Typography variant="body2" className={classes.muted}>n/d</Typography>
    )}
  </div>
);

const ChampAnalyse = ({ libelle, valeur }) => (
  <div>
    <Typography variant="caption" style={{ color: '#9ca3af', textTransform: 'uppercase', fontSize: 10 }}>{libelle}</Typography>
    <Typography variant="body2" style={{ fontSize: 12, wordBreak: 'break-word' }}>{texte(valeur)}</Typography>
  </div>
);

const CarteSignalAnalyse = ({ carte, classes, ouvrirEpisodes }) => {
  const { signal, nbOccurrences, premierTimestamp, dernierTimestamp, preuveObs, episodesImpliques } = carte;
  const fluxImpactes = (signal.flux_affectes || []).filter(Boolean).join(', ');
  return (
    <div className={classes.carteSignalAnalyse}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 4 }}>
        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>{signal.nom}</Typography>
        <Chip size="small" label={texte(signal.criticite)} className={classeCriticiteSignal(classes, signal.criticite)} />
        <Chip size="small" label={`confiance ${texte(signal.confiance)}`} className={classeConfiance(classes, signal.confiance)} />
        <Typography variant="caption" className={classes.muted}>{nbOccurrences} occurrence{nbOccurrences !== 1 ? 's' : ''}</Typography>
      </div>
      <Typography variant="body2" style={{ fontSize: 13 }}>{texte(signal.description)}</Typography>
      <div className={classes.carteGrille}>
        <ChampAnalyse libelle="Preuve observee" valeur={preuveObs} />
        <ChampAnalyse libelle="Premiere occurrence" valeur={formaterDate(premierTimestamp)} />
        {dernierTimestamp && dernierTimestamp !== premierTimestamp && (
          <ChampAnalyse libelle="Derniere occurrence" valeur={formaterDate(dernierTimestamp)} />
        )}
        {fluxImpactes && <ChampAnalyse libelle="Impact flux" valeur={fluxImpactes} />}
      </div>
      {signal.actions_recommandees && signal.actions_recommandees.length > 0 && (
        <Typography variant="body2" style={{ fontSize: 12, marginTop: 4 }}>
          <strong>Action :</strong> {signal.actions_recommandees[0]}
        </Typography>
      )}
      {episodesImpliques.length > 0 && (
        <div className={classes.carteActions}>
          <Button size="small" variant="outlined" onClick={() => ouvrirEpisodes(episodesImpliques)}>
            Voir episode{episodesImpliques.length > 1 ? 's' : ''} ({episodesImpliques.length})
          </Button>
        </div>
      )}
    </div>
  );
};

const CarteBoucleAnalyse = ({ carte, classes, ouvrirEpisodes }) => {
  const { nom, signaux, dureeMs, dureePhraseFormatee, episodesImpliques } = carte;
  return (
    <div className={classes.carteBoucleAnalyse}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 4 }}>
        <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#15803d' }}>{nom}</Typography>
        <Chip size="small" label={`${signaux.length} signal${signaux.length > 1 ? 'x' : ''}`} className={classes.chipNormal} />
        {dureeMs > 0 && <Typography variant="caption" className={classes.muted}>{dureePhraseFormatee}</Typography>}
      </div>
      <Typography variant="body2" style={{ fontSize: 12 }}>
        Signaux : {signaux.map((s) => s.nom).join(' · ')}
      </Typography>
      {episodesImpliques.length > 0 && (
        <div className={classes.carteActions}>
          <Button size="small" variant="outlined" onClick={() => ouvrirEpisodes(episodesImpliques)}>
            Voir episode{episodesImpliques.length > 1 ? 's' : ''} ({episodesImpliques.length})
          </Button>
        </div>
      )}
    </div>
  );
};

const CarteFluxAnalyse = ({ carte, classes, ouvrirEpisodes }) => {
  const { nom, signaux, criticiteMax, episodesImpliques } = carte;
  const classeEtat = criticiteMax === 'critique' ? classes.chipBlocked : criticiteMax === 'degrade' ? classes.chipWatch : classes.chipNormal;
  const etatLabel = criticiteMax === 'critique' ? 'degrade' : criticiteMax === 'degrade' ? 'surveiller' : 'actif';
  return (
    <div className={classes.carteFluxAnalyse}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 4 }}>
        <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#1d4ed8' }}>{nom}</Typography>
        <Chip size="small" label={etatLabel} className={classeEtat} />
        <Typography variant="caption" className={classes.muted}>{signaux.length} signal{signaux.length > 1 ? 'x' : ''}</Typography>
      </div>
      <Typography variant="body2" style={{ fontSize: 12 }}>
        {signaux.map((s) => s.nom).join(', ')}
      </Typography>
      {episodesImpliques.length > 0 && (
        <div className={classes.carteActions}>
          <Button size="small" variant="outlined" onClick={() => ouvrirEpisodes(episodesImpliques)}>
            Voir episode{episodesImpliques.length > 1 ? 's' : ''} ({episodesImpliques.length})
          </Button>
        </div>
      )}
    </div>
  );
};

const SectionAnalyse = ({ signauxReconnus, timeline, episodesRuntime, classes, ouvrirEpisodes }) => {
  const analyse = useMemo(
    () => construireAnalyse(signauxReconnus, timeline, episodesRuntime),
    [signauxReconnus, timeline, episodesRuntime],
  );

  if (!analyse.cartesSignal.length && !analyse.cartesBoucle.length && !analyse.cartesFlux.length) return null;

  return (
    <section className={classes.section}>
      <Typography variant="h6">Analyse</Typography>
      <Typography variant="body2" className={classes.muted}>
        Synthèse par signal, boucle et flux reconnus. Les épisodes détaillés sont en dessous.
      </Typography>

      {analyse.cartesSignal.length > 0 && (
        <div className={classes.section}>
          <Typography variant="subtitle2">Signaux ({analyse.cartesSignal.length})</Typography>
          {analyse.cartesSignal.map((carte) => (
            <CarteSignalAnalyse
              key={carte.signal.id || carte.signal.nom}
              carte={carte}
              classes={classes}
              ouvrirEpisodes={ouvrirEpisodes}
            />
          ))}
        </div>
      )}

      {analyse.cartesBoucle.length > 0 && (
        <div className={classes.section}>
          <Typography variant="subtitle2">Boucles ({analyse.cartesBoucle.length})</Typography>
          {analyse.cartesBoucle.map((carte) => (
            <CarteBoucleAnalyse
              key={carte.nom}
              carte={carte}
              classes={classes}
              ouvrirEpisodes={ouvrirEpisodes}
            />
          ))}
        </div>
      )}

      {analyse.cartesFlux.length > 0 && (
        <div className={classes.section}>
          <Typography variant="subtitle2">Flux ({analyse.cartesFlux.length})</Typography>
          {analyse.cartesFlux.map((carte) => (
            <CarteFluxAnalyse
              key={carte.nom}
              carte={carte}
              classes={classes}
              ouvrirEpisodes={ouvrirEpisodes}
            />
          ))}
        </div>
      )}
    </section>
  );
};

const SectionBdLogOperationnel = ({ etatVideo, classes }) => {
  const resume = etatVideo.resume || {};
  const anomalies = etatVideo.anomalies || [];
  const timeline = etatVideo.timeline || [];
  const rawTresLongs = etatVideo.rawTresLongs || [];
  const croisements = etatVideo.croisementsRawCut || [];

  if (!etatVideo || (!etatVideo.dernierSnapshotAt && (!etatVideo.snapshots || etatVideo.snapshots.length === 0))) {
    return (
      <div className={`${classes.callout} ${classes.section}`}>
        <Typography variant="body2">Aucun BDLogFile disponible pour cette camera dans le focus courant.</Typography>
      </div>
    );
  }

  return (
    <div className={classes.section}>
      <Grid container spacing={1}>
        <Grid item xs={6} sm={4}><ChampFocus libelle="RAW" valeur={nombre(resume.nbRawRecents)} /></Grid>
        <Grid item xs={6} sm={4}><ChampFocus libelle="Videos" valeur={nombre(resume.nbVideosDecoupees)} /></Grid>
        <Grid item xs={6} sm={4}><ChampFocus libelle="Pending 202" valeur={nombre(resume.nbPending202)} /></Grid>
        <Grid item xs={6} sm={4}><ChampFocus libelle="Succes 1212" valeur={nombre(resume.nbSucces1212)} /></Grid>
        <Grid item xs={6} sm={4}><ChampFocus libelle="Succes 1213" valeur={nombre(resume.nbSucces1213)} /></Grid>
        <Grid item xs={6} sm={4}><ChampFocus libelle="Erreurs 2626" valeur={nombre(resume.nbErreurs2626)} /></Grid>
        <Grid item xs={6} sm={4}><ChampFocus libelle="Convergence" valeur={nombre(resume.nbConvergences)} /></Grid>
        <Grid item xs={6} sm={4}><ChampFocus libelle="RAW/CUT" valeur={nombre(resume.nbRelationsRawCut)} /></Grid>
        <Grid item xs={12} sm={4}><ChampFocus libelle="Dernier BDLog" valeur={formaterDate(etatVideo.dernierSnapshotAt)} /></Grid>
      </Grid>

      {anomalies.length > 0 && (
        <div className={`${classes.warning} ${classes.section}`}>
          <Typography variant="subtitle2">Anomalies video simples</Typography>
          {anomalies.slice(0, 5).map((anomalie, index) => (
            <div key={index} style={{ marginTop: 4 }}>
              <Chip size="small" label={texte(anomalie.niveau || anomalie.type)} className={classeStagnation(classes, anomalie.niveau)} />
              <span style={{ marginLeft: 8 }}>{texte(anomalie.message || anomalie.extrait)}</span>
            </div>
          ))}
        </div>
      )}

      {rawTresLongs.length > 0 && (
        <section className={classes.section}>
          <Typography variant="subtitle2">RAW tres longs</Typography>
          <Table size="small">
            <TableBody>
              {rawTresLongs.slice(0, 5).map((item) => (
                <TableRow key={`raw-long-${item.numeroLigne}`}>
                  <TableCell>ligne {item.numeroLigne}</TableCell>
                  <TableCell>{item.dureeSecondes ? `${item.dureeSecondes}s` : 'duree n/d'}</TableCell>
                  <TableCell>{tronquer(texte(item.extrait), 120)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </section>
      )}

      {croisements.length > 0 && (
        <section className={classes.section}>
          <Typography variant="subtitle2">Croisements RAW / CUT</Typography>
          <Table size="small">
            <TableBody>
              {croisements.slice(0, 6).map((item) => (
                <TableRow key={`raw-cut-${item.numeroLigne}`}>
                  <TableCell>{texte(item.ids && (item.ids.baseDocId || item.ids.rawDocId))}</TableCell>
                  <TableCell>{texte(item.ids && item.ids.cutDocId)}</TableCell>
                  <TableCell>{tronquer(texte(item.extrait), 120)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </section>
      )}

      {timeline.length > 0 && (
        <section className={classes.section}>
          <Typography variant="subtitle2">Timeline BDLog</Typography>
          <Table size="small">
            <TableBody>
              {timeline.slice(0, 10).map((item) => (
                <TableRow key={`bdlog-${item.numeroLigne}`}>
                  <TableCell>{item.timestamp ? formaterHeure(item.timestamp) : `ligne ${item.numeroLigne}`}</TableCell>
                  <TableCell><Chip size="small" label={libelleTypeBdLog(item.type)} className={classeTypeBdLog(classes, item.type)} /></TableCell>
                  <TableCell>{item.etatSync ? `etat ${item.etatSync}` : tronquer(texte(item.extrait), 150)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </section>
      )}

      {(etatVideo.indices || []).length > 0 && (
        <details className={classes.section}>
          <summary>Indices BDLog bruts</summary>
          <pre className={classes.preClair}>{etatVideo.indices.join('\n')}</pre>
        </details>
      )}
    </div>
  );
};

const ChampFocus = ({ libelle, valeur }) => (
  <div>
    <Typography variant="caption" style={{ color: '#6b7280', textTransform: 'uppercase' }}>{libelle}</Typography>
    <Typography variant="body2" style={{ fontWeight: 700, wordBreak: 'break-word' }}>{texte(valeur)}</Typography>
  </div>
);

const SectionPreuveBruteImmediate = ({ resultat, classes, indexSignauxDocumentaires }) => {
  const evenement = extraireEvenementBrutPrioritaire(resultat);
  const preuveTimeline = extrairePreuveTimelinePrioritaire(resultat);

  if (!evenement && !preuveTimeline) {
    return (
      <section className={classes.section}>
        <Typography variant="h6">Preuve brute immediate</Typography>
        <Typography className={classes.muted}>Aucune ligne brute exposee pour cette recherche.</Typography>
      </section>
    );
  }

  const source = evenement ? {
    titre: evenement.evenement_type || evenement.niveau || 'evenement source',
    timestamp: evenement.ts_event || evenement.recu_at,
    camera: evenement.cam_id || evenement.tel_id,
    chemin: [evenement.remote_path, evenement.nom_fichier, evenement.numero_ligne ? `ligne ${evenement.numero_ligne}` : null].filter(Boolean).join(' | '),
    ligne: evenement.contenu_brut,
    payload: evenement.payload,
  } : {
    titre: preuveTimeline.etat || preuveTimeline.signal || 'preuve timeline',
    timestamp: preuveTimeline.timestamp,
    camera: preuveTimeline.objet && (preuveTimeline.objet.libelle || preuveTimeline.objet.id),
    chemin: preuveTimeline.preuve && preuveTimeline.preuve.source,
    ligne: preuveTimeline.preuve && preuveTimeline.preuve.valeur,
    payload: preuveTimeline.preuve,
  };
  const signalDocumentaire = trouverSignalDansTexte(indexSignauxDocumentaires, `${source.titre} ${source.ligne}`);

  return (
    <section className={classes.section}>
      <Typography variant="h6">Preuve brute immediate</Typography>
      <div className={classes.callout} style={{ marginBottom: 8 }}>
        <Typography variant="body2">
          <strong>{texte(source.titre)}</strong>
          {source.timestamp && <span className={classes.muted}> | {formaterDate(source.timestamp)}</span>}
          {source.camera && <span className={classes.muted}> | {source.camera}</span>}
          {source.chemin && <span className={classes.muted}> | {source.chemin}</span>}
        </Typography>
      </div>
      <pre className={classes.pre}>{texte(source.ligne)}</pre>
      <FicheSignalDocumentaire signal={signalDocumentaire} classes={classes} />
      {source.payload && (
        <pre className={classes.pre} style={{ marginTop: 8 }}>{JSON.stringify(source.payload, null, 2)}</pre>
      )}
    </section>
  );
};

const SectionCarteObjet = ({ resultat, classes }) => {
  const dernierSucces = resultat.dernierSuccesProuve;
  const derniereActivite = (resultat.timelineOrdonnee || []).find((item) => item.timestamp) || null;
  const relations = (resultat.voisinageRelationnel && resultat.voisinageRelationnel.relations) || [];
  const matchRelation = relations.find((r) => r.cibleId && r.cibleId.toLowerCase().includes('match'))
    || relations.find((r) => r.type && r.type.toLowerCase().includes('match'));
  const evenements = (resultat.sources && resultat.sources.evenements) || [];
  const versionApk = evenements.reduce((acc, e) => acc || e.version_apk || e.versionApk || null, null)
    || (resultat.resumeOperationnel && resultat.resumeOperationnel.versionApk)
    || null;
  const anomalies = resultat.anomaliesStagnations || [];
  const prochainEtat = resultat.prochainEtatAttendu;

  return (
    <section className={classes.section}>
      <Table size="small">
        <TableBody>
          <TableRow>
            <TableCell className={classes.labelCarte}><strong>Dernier succes prouve</strong></TableCell>
            <TableCell>
              {dernierSucces && dernierSucces.dernier_etat_prouve ? (
                <span>
                  <Chip size="small" label={dernierSucces.dernier_etat_prouve} className={classes.chipNormal} />
                  {dernierSucces.preuve && dernierSucces.preuve.timestamp && (
                    <span className={classes.muted} style={{ marginLeft: 8 }}>
                      {formaterDate(dernierSucces.preuve.timestamp)}
                    </span>
                  )}
                </span>
              ) : (
                <span className={classes.muted}>Aucun succes prouve dans le flux</span>
              )}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.labelCarte}><strong>Derniere activite</strong></TableCell>
            <TableCell>
              {derniereActivite ? (
                <span>
                  <span style={{ fontFamily: 'monospace' }}>{formaterHeure(derniereActivite.timestamp)}</span>
                  <span style={{ marginLeft: 8 }}>{texte(derniereActivite.signal)}</span>
                  {derniereActivite.confiance === 'faible' && (
                    <Chip size="small" label="confiance faible" className={classes.chipWatch} style={{ marginLeft: 8 }} />
                  )}
                </span>
              ) : (
                <span className={classes.muted}>Aucune activite detectee</span>
              )}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.labelCarte}><strong>Dernier match associe</strong></TableCell>
            <TableCell>
              {matchRelation ? (
                <span style={{ fontFamily: 'monospace' }}>
                  {matchRelation.cibleId}
                  {matchRelation.etat && matchRelation.etat !== 'actif' && (
                    <span className={classes.muted}> — {matchRelation.etat}</span>
                  )}
                </span>
              ) : (
                <span className={classes.muted}>Non identifie dans le voisinage</span>
              )}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.labelCarte}><strong>Version APK</strong></TableCell>
            <TableCell>
              {versionApk ? (
                <span style={{ fontFamily: 'monospace' }}>{versionApk}</span>
              ) : (
                <span className={classes.muted}>n/d</span>
              )}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.labelCarte}><strong>Anomalies</strong></TableCell>
            <TableCell>
              {anomalies.length > 0 ? (
                <div>
                  {anomalies.slice(0, 4).map((a, i) => (
                    <span key={i} style={{ marginRight: 6 }}>
                      <Chip size="small" label={a.type || a.niveau || 'anomalie'} className={classeStagnation(classes, a.niveau)} />
                    </span>
                  ))}
                  {anomalies.length > 4 && (
                    <span className={classes.muted}> +{anomalies.length - 4} autres (voir debug)</span>
                  )}
                </div>
              ) : (
                <Chip size="small" label="Aucune anomalie" className={classes.chipNormal} />
              )}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.labelCarte}><strong>Prochaine transition attendue</strong></TableCell>
            <TableCell>
              {prochainEtat ? (
                <span>
                  <strong>{prochainEtat.etat}</strong>
                  {prochainEtat.preuve_manquante && (
                    <span className={classes.muted} style={{ marginLeft: 8 }}>
                      — preuve manquante: {texte(prochainEtat.preuve_manquante)}
                    </span>
                  )}
                </span>
              ) : (
                <span className={classes.muted}>Non determinee</span>
              )}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </section>
  );
};

const SectionTimelineHumaine = ({ items, classes }) => (
  <section className={classes.section}>
    <Typography variant="h6">Timeline</Typography>
    {items && items.length > 0 ? (
      <div>
        {items.slice(0, 50).map((item, index) => (
          <div key={index} className={classes.ligneTimeline}>
            <span className={classes.heureTimeline}>{formaterHeure(item.timestamp)}</span>
            <span className={classes.signalTimeline}>{texte(item.signal)}</span>
            {item.confiance === 'faible' && (
              <Chip size="small" label="?" className={classes.chipWatch} style={{ height: 16, fontSize: 10 }} />
            )}
          </div>
        ))}
        {items.length > 50 && (
          <Typography variant="body2" className={classes.muted} style={{ marginTop: 4 }}>
            ... {items.length - 50} entrees supplementaires disponibles dans le debug.
          </Typography>
        )}
      </div>
    ) : (
      <Typography className={classes.muted}>Aucune activite dans le flux inspecte.</Typography>
    )}
  </section>
);

const SectionRelationsVisibles = ({ voisinage, relationsManquantes, classes }) => {
  const relations = voisinage && voisinage.relations ? voisinage.relations : [];

  return (
    <section className={classes.section}>
      <Typography variant="h6">Relations</Typography>
      {relations.length > 0 ? (
        <div>
          {relations.slice(0, 25).map((rel, index) => (
            <div key={rel.id || index} className={classes.fluxRelation}>
              <span>{rel.sourceId}</span>
              <span className={classes.fleche}>→</span>
              <span>{rel.cibleId}</span>
              {rel.type && <span className={classes.muted}>({rel.type})</span>}
              {rel.etat && rel.etat !== 'actif' && (
                <Chip size="small" label={rel.etat} className={classeStagnation(classes, rel.etat)} />
              )}
            </div>
          ))}
        </div>
      ) : (
        <Typography className={classes.muted}>Aucune relation exposee.</Typography>
      )}
      {relationsManquantes && relationsManquantes.length > 0 && (
        <div className={classes.warning + ' ' + classes.section}>
          <Typography variant="subtitle2">Liens manquants</Typography>
          {relationsManquantes.slice(0, 5).map((item, index) => (
            <div key={index} className={classes.fluxRelation}>
              <span>{item.objetId}</span>
              {item.type && (
                <span>
                  <span className={classes.fleche}>→</span>
                  <span>{item.type}</span>
                </span>
              )}
              {item.message && <span className={classes.muted}>— {item.message}</span>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const SectionTracesLocales = ({ traces, classes }) => (
  <section className={classes.section}>
    <Typography variant="h6">Traces locales recentes</Typography>
    <Typography variant="body2" className={classes.muted}>
      Instrumentation minimale pour observer les recherches et objets consultes.
    </Typography>
    <Table size="small">
      <TableBody>
        {traces.slice(0, 8).map((trace, index) => (
          <TableRow key={trace.timestamp + '-' + index}>
            <TableCell>{formaterDate(trace.timestamp)}</TableCell>
            <TableCell>{trace.type}</TableCell>
            <TableCell>{JSON.stringify(trace.details || {})}</TableCell>
          </TableRow>
        ))}
        {traces.length === 0 && (
          <TableRow>
            <TableCell>Aucune trace locale pour le moment.</TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </section>
);

const METRIQUES_PAR_SOURCE = {
  'monitoring_sync_cam_evenements_classes': 'nbEvenements',
  'monitoring_sync_cam_incidents_recents': 'nbEvenements',
  'monitoring_sync_cam_journee_camera': 'nbJournees',
  'monitoring_sync_cam_tendance_7j': 'nbTendances',
  'monitoring_graphe_objet': 'nbObjetsGraphe',
  'monitoring_graphe_relation': 'nbRelationsGraphe',
};

const SectionDebugSources = ({ payload, resultats, classes }) => {
  const sources = payload.sourcesSql || [];
  const avertissements = payload.avertissements || [];
  const metriques = payload.metriques || {};

  const sourcesAbsentes = avertissements
    .filter((a) => a.endsWith('_indisponible'))
    .map((a) => a.replace('_indisponible', ''));

  const tousEvenements = (resultats || []).flatMap((r) => (r.sources && r.sources.evenements) || []);
  const derniereDate = tousEvenements
    .map((e) => e.ts_event || e.recu_at)
    .filter(Boolean)
    .sort()
    .slice(-1)[0] || null;

  const nbObjetsReels = metriques.nbResultats !== undefined ? metriques.nbResultats : (resultats || []).length;
  const systemeToucheVivant = sourcesAbsentes.length < sources.length
    && (metriques.nbEvenements > 0 || metriques.nbJournees > 0 || metriques.nbObjetsGraphe > 0);

  return (
    <section className={classes.section}>
      <Typography variant="h6">Debug sources</Typography>
      {systemeToucheVivant ? (
        <div className={classes.callout} style={{ marginBottom: 8 }}>
          <Typography variant="body2">Systeme raccorde au vivant — donnees runtime lues depuis la base.</Typography>
        </div>
      ) : (
        <div className={classes.warning} style={{ marginBottom: 8 }}>
          <Typography variant="body2">Systeme non raccorde — aucune donnee runtime lue.</Typography>
        </div>
      )}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Source SQL</TableCell>
            <TableCell>Statut</TableCell>
            <TableCell>Lignes lues</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sources.map((source) => {
            const cle = METRIQUES_PAR_SOURCE[source];
            const nb = cle !== undefined ? metriques[cle] : null;
            return (
              <TableRow key={source}>
                <TableCell style={{ fontFamily: 'monospace', fontSize: 11 }}>{source}</TableCell>
                <TableCell>
                  {sourcesAbsentes.includes(source)
                    ? <Chip size="small" label="vue absente" className={classes.chipBlocked} />
                    : <Chip size="small" label="reachable" className={classes.chipNormal} />}
                </TableCell>
                <TableCell>{nb !== null && nb !== undefined ? texte(nb) : 'n/d'}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Table size="small" className={classes.section}>
        <TableBody>
          <LigneLibelleValeur libelle="Derniere date runtime" valeur={derniereDate ? formaterDate(derniereDate) : 'aucune donnee'} />
          <LigneLibelleValeur libelle="Objets reels trouves" valeur={nbObjetsReels} />
          <LigneLibelleValeur libelle="Evenements lus" valeur={metriques.nbEvenements} />
          <LigneLibelleValeur libelle="Journees camera" valeur={metriques.nbJournees} />
          <LigneLibelleValeur libelle="Objets graphe" valeur={metriques.nbObjetsGraphe} />
          <LigneLibelleValeur libelle="Relations graphe" valeur={metriques.nbRelationsGraphe} />
        </TableBody>
      </Table>
      {sourcesAbsentes.length > 0 && (
        <div className={classes.warning + ' ' + classes.section}>
          <Typography variant="subtitle2">Cause: migrations SQL non appliquees sur le serveur</Typography>
          <Typography variant="body2">
            Vues manquantes: {sourcesAbsentes.join(', ')}.
            Les fichiers 002_sync_cam_projection_operationnelle.sql et 003_graphe_vivant_operationnel.sql
            existent dans reactpournode/outils/monitoring_sync_cam/migrations/ mais n'ont pas ete
            copies dans infraascode/roles/monitoring/files/migrations/. Ansible ne les applique pas.
          </Typography>
        </div>
      )}
    </section>
  );
};

const SectionContrat = ({ payload, classes }) => {
  const metriques = payload.metriques || {};
  const avertissements = payload.avertissements || [];

  return (
    <section>
      <Typography variant="h6">Reponse backend</Typography>
      <Typography variant="body2" className={classes.muted}>
        Contrat {texte(payload.versionContrat)} | requete {texte(payload.requete)} | profondeur {texte(payload.profondeur)}
      </Typography>
      <Grid container spacing={2} className={classes.section}>
        {Object.keys(metriques).map((cle) => (
          <Grid item xs={6} sm={4} md={2} key={cle}>
            <Typography variant="caption" className={classes.muted}>{cle}</Typography>
            <Typography variant="h6">{texte(metriques[cle])}</Typography>
          </Grid>
        ))}
        {Object.keys(metriques).length === 0 && (
          <Grid item xs={12}>
            <Typography className={classes.muted}>Aucune metrique exposee par le backend.</Typography>
          </Grid>
        )}
      </Grid>
      {avertissements.length > 0 && (
        <div className={classes.warning + ' ' + classes.section}>
          <Typography variant="subtitle2">Avertissements backend</Typography>
          <Typography variant="body2">{avertissements.join(', ')}</Typography>
        </div>
      )}
    </section>
  );
};

const LigneLibelleValeur = ({ libelle, valeur }) => (
  <TableRow>
    <TableCell><strong>{libelle}</strong></TableCell>
    <TableCell>{texte(valeur)}</TableCell>
  </TableRow>
);

const SectionResume = ({ items, classes }) => (
  <section className={classes.section}>
    <Typography variant="h6">Resume narratif</Typography>
    {items && items.length > 0 ? (
      <ul className={classes.denseList}>
        {items.map((item, index) => (
          <li key={index}>
            <Chip size="small" label={item.statut || 'inconnu'} className={classeStatut(classes, item.statut)} /> {item.texte}
          </li>
        ))}
      </ul>
    ) : <Typography className={classes.muted}>Aucun resume narratif expose.</Typography>}
  </section>
);

const SectionAnomalies = ({ items, classes }) => (
  <section className={classes.section}>
    <Typography variant="h6">Anomalies / stagnations (detail)</Typography>
    <Table size="small">
      <TableBody>
        {(items || []).slice(0, 20).map((item, index) => (
          <TableRow key={index}>
            <TableCell>{item.objetId || item.relationId || 'n/d'}</TableCell>
            <TableCell>{item.type}</TableCell>
            <TableCell>
              {item.niveau && <Chip size="small" label={item.niveau} className={classeStagnation(classes, item.niveau)} />}
              {!item.niveau && texte(item.message)}
            </TableCell>
            <TableCell>{texte(item.current_state)}</TableCell>
            <TableCell>{texte(item.missing_evidence)}</TableCell>
          </TableRow>
        ))}
        {(!items || items.length === 0) && <TableRow><TableCell>Aucune anomalie exposee.</TableCell></TableRow>}
      </TableBody>
    </Table>
  </section>
);

const SectionVoisinage = ({ valeur, classes }) => {
  const objets = valeur && valeur.objets ? valeur.objets : [];
  const relations = valeur && valeur.relations ? valeur.relations : [];

  return (
    <section className={classes.section}>
      <Typography variant="h6">Voisinage relationnel (brut)</Typography>
      <Typography variant="body2" className={classes.muted}>{objets.length} objets, {relations.length} relations.</Typography>
      <Table size="small">
        <TableBody>
          {relations.slice(0, 30).map((relation) => (
            <TableRow key={relation.id}>
              <TableCell>{relation.sourceId}</TableCell>
              <TableCell>{relation.type}</TableCell>
              <TableCell>{relation.cibleId}</TableCell>
              <TableCell>{relation.etat}</TableCell>
            </TableRow>
          ))}
          {relations.length === 0 && <TableRow><TableCell>Aucune relation exposee.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </section>
  );
};

const SectionPreuves = ({ items, classes }) => (
  <section className={classes.section}>
    <Typography variant="h6">Preuves runtime</Typography>
    <Table size="small">
      <TableBody>
        {(items || []).slice(0, 60).map((item, index) => (
          <TableRow key={index}>
            <TableCell>{item.objetId}</TableCell>
            <TableCell>{item.typeObjet}</TableCell>
            <TableCell>{textePreuve(item.preuve)}</TableCell>
          </TableRow>
        ))}
        {(!items || items.length === 0) && <TableRow><TableCell>Aucune preuve exposee.</TableCell></TableRow>}
      </TableBody>
    </Table>
  </section>
);

const texte = (valeur) => {
  if (valeur === undefined || valeur === null || valeur === '') return 'n/d';
  if (Array.isArray(valeur)) return valeur.filter(Boolean).join(', ');
  if (typeof valeur === 'object') return JSON.stringify(valeur);
  return String(valeur);
};

const lireRapportsReprojectionMemorises = () => {
  if (typeof window === 'undefined' || !window.localStorage) return {};
  try {
    return JSON.parse(window.localStorage.getItem(CLE_RAPPORT_REPROJECTION) || '{}');
  } catch (erreur) {
    return {};
  }
};

const lireRapportReprojectionSnapshot = (snapshotId) => {
  const rapports = lireRapportsReprojectionMemorises();
  const entree = rapports[String(snapshotId)];
  return entree && entree.rapport ? entree.rapport : null;
};

const memoriserRapportReprojectionSnapshot = (snapshotId, rapport) => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    const rapports = lireRapportsReprojectionMemorises();
    rapports[String(snapshotId)] = {
      snapshotId,
      memoriseAt: new Date().toISOString(),
      rapport,
    };
    window.localStorage.setItem(CLE_RAPPORT_REPROJECTION, JSON.stringify(rapports));
  } catch (erreur) {
    // Le rapport reste affiche en memoire meme si le stockage local est indisponible.
  }
};

const extraireCameraDepuisRecherche = (valeur) => {
  const q = String(valeur || '').trim();
  if (/^\d{1,8}$/.test(q)) return q;
  const match = q.match(/^(?:camera|cam)[\s:_-]*([A-Za-z0-9_.-]{1,80})$/i);
  return match ? match[1] : '';
};

const nombre = (valeur) => {
  const n = Number(valeur || 0);
  return Number.isFinite(n) ? n.toLocaleString('fr-CA') : '0';
};

const tronquer = (valeur, taille) => {
  const texteValeur = String(valeur || '');
  return texteValeur.length > taille ? `${texteValeur.slice(0, taille)}...` : texteValeur;
};

const textePreuve = (preuve) => {
  if (!preuve) return 'n/d';
  return [preuve.type, texte(preuve.valeur), preuve.timestamp, preuve.source].filter(Boolean).join(' | ');
};

const extraireEvenementBrutPrioritaire = (resultat) => {
  const evenements = resultat && resultat.sources && Array.isArray(resultat.sources.evenements)
    ? resultat.sources.evenements
    : [];
  if (evenements.length === 0) return null;

  return [...evenements].sort((a, b) => timestampMs(b.ts_event || b.recu_at) - timestampMs(a.ts_event || a.recu_at))[0];
};

const extrairePreuveTimelinePrioritaire = (resultat) => {
  const timeline = resultat && Array.isArray(resultat.timelineOrdonnee) ? resultat.timelineOrdonnee : [];
  return [...timeline]
    .filter((item) => item && item.preuve && (item.preuve.valeur || item.preuve.source))
    .sort((a, b) => timestampMs(b.timestamp) - timestampMs(a.timestamp))[0] || null;
};

const timestampMs = (valeur) => {
  if (!valeur) return 0;
  const date = new Date(valeur);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const formaterDate = (valeur) => {
  if (!valeur) return 'n/d';
  const date = new Date(valeur);
  return Number.isNaN(date.getTime()) ? String(valeur) : date.toLocaleString();
};

const formaterHeure = (valeur) => {
  if (!valeur) return '--:--';
  const date = new Date(valeur);
  if (Number.isNaN(date.getTime())) return String(valeur).slice(0, 5);
  return date.getHours().toString().padStart(2, '0') + ':' + date.getMinutes().toString().padStart(2, '0');
};

const heuresDepuisContexte = (contexte) => {
  const debutMs = timestampMs(contexte && contexte.debut);
  const finMs = timestampMs(contexte && contexte.fin);
  return {
    heureDebut: debutMs ? formaterHeure(new Date(Math.max(0, debutMs - 60 * 1000)).toISOString()) : '',
    heureFin: finMs ? formaterHeure(new Date(finMs + 60 * 1000).toISOString()) : '',
  };
};

const trouverEpisodeCible = (episodes, contexte) => {
  const debutMs = timestampMs(contexte && contexte.debut);
  const finMs = timestampMs(contexte && contexte.fin) || debutMs;
  const incident = String(contexte && contexte.incident || '');
  if (incident) {
    const parIncident = (episodes || []).find((episode) => (
      (episode.evenements || []).some((ligne) => String(ligne.ligne_id || ligne.id || '') === incident)
    ));
    if (parIncident) return parIncident.id;
  }
  if (!debutMs && !finMs) return '';
  const parFenetre = (episodes || []).find((episode) => (
    episode.debut <= finMs && episode.fin >= debutMs
  ));
  return parFenetre ? parFenetre.id : '';
};

const classeStatut = (classes, statut) => {
  if (statut === 'prouve') return classes.chipNormal;
  if (statut === 'contredit') return classes.chipBlocked;
  if (statut === 'inconnu' || statut === 'suppose') return classes.chipWatch;
  return classes.chipUnknown;
};

const classeStagnation = (classes, niveau) => {
  if (niveau === 'normal') return classes.chipNormal;
  if (niveau === 'bloque' || niveau === 'critique') return classes.chipBlocked;
  if (niveau === 'a_surveiller' || niveau === 'surveillance') return classes.chipWatch;
  return classes.chipUnknown;
};

const libelleTypeBdLog = (type) => {
  if (type === 'relation_raw_cut') return 'RAW/CUT';
  if (type === 'pending') return 'pending';
  if (type === 'erreur') return 'erreur';
  if (type === 'raw') return 'RAW';
  if (type === 'video') return 'video';
  if (type === 'convergence') return 'convergence';
  return 'indice';
};

const classeTypeBdLog = (classes, type) => {
  if (type === 'erreur') return classes.chipBlocked;
  if (type === 'pending') return classes.chipWatch;
  if (type === 'raw' || type === 'video' || type === 'relation_raw_cut' || type === 'convergence') return classes.chipNormal;
  return classes.chipUnknown;
};

const classeCriticiteSignal = (classes, criticite) => {
  const valeur = String(criticite || '').toLowerCase();
  if (valeur === 'rouge' || valeur === 'critique') return classes.chipBlocked;
  if (valeur === 'orange' || valeur === 'jaune') return classes.chipWatch;
  if (valeur === 'vert' || valeur === 'verte') return classes.chipNormal;
  return classes.chipUnknown;
};

const classeConfiance = (classes, confiance) => {
  if (confiance === 'haute' || confiance === 'elevee') return classes.chipNormal;
  if (confiance === 'faible') return classes.chipWatch;
  if (confiance === 'moyen' || confiance === 'moyenne') return classes.chipWatch;
  return classes.chipUnknown;
};

const FENETRE_EPISODE_CAMERA_MS = 15 * 60 * 1000;
const DUREE_MAX_EPISODE_CAMERA_MS = 45 * 60 * 1000;

const extraireOptionsFiltresTimeline = (timeline) => {
  const options = {
    severites: new Set(),
    evenements: new Set(),
    sources: new Set(),
    domaines: new Set(),
  };

  (timeline || []).forEach((ligne) => {
    ajouterOption(options.severites, ligne.niveau);
    ajouterOption(options.evenements, ligne.event || ligne.evenement_type);
    ajouterOption(options.sources, ligne.source);
    ajouterOption(options.domaines, ligne.domaine);
  });

  return {
    severites: trierOptions(options.severites),
    evenements: trierOptions(options.evenements),
    sources: trierOptions(options.sources),
    domaines: trierOptions(options.domaines),
  };
};

const ajouterOption = (set, valeur) => {
  const normalisee = String(valeur || '').trim();
  if (normalisee && normalisee !== 'n/d') set.add(normalisee);
};

const trierOptions = (set) => Array.from(set).sort((a, b) => a.localeCompare(b, 'fr'));

const filtrerTimelineCamera = (timeline, filtres, journee) => (timeline || []).filter((ligne) => {
  const corpus = [
    ligne.timestamp,
    ligne.niveau,
    ligne.event,
    ligne.evenement_type,
    ligne.source,
    ligne.domaine,
    ligne.contenu_brut,
  ].map((valeur) => String(valeur || '').toLowerCase()).join(' ');
  const ts = timestampMs(ligne.timestamp);
  const debut = filtres.heureDebut ? timestampHeureDansJournee(filtres.heureDebut, journee, ligne.timestamp) : 0;
  const fin = filtres.heureFin ? timestampHeureDansJournee(filtres.heureFin, journee, ligne.timestamp) : 0;

  if (filtres.severite && String(ligne.niveau || '') !== filtres.severite) return false;
  if (filtres.evenement && String(ligne.event || ligne.evenement_type || '') !== filtres.evenement) return false;
  if (filtres.source && String(ligne.source || '') !== filtres.source) return false;
  if (filtres.domaine && String(ligne.domaine || '') !== filtres.domaine) return false;
  if (filtres.texte && !corpus.includes(filtres.texte.toLowerCase())) return false;
  if (debut && ts && ts < debut) return false;
  if (fin && ts && ts > fin) return false;
  if (filtres.mode === 'critique' && !/error|critical|critique|fatal|blocked|bloque|2626/.test(corpus)) return false;
  if (filtres.mode === 'convergence' && !/convergence|raw|first_bytes|copy|video_copy|202/.test(corpus)) return false;
  if (filtres.mode === 'recovery' && !/recovery|restart|overlap|deferred|recording_state/.test(corpus)) return false;
  if (filtres.mode === 'erreur' && !/error|erreur|exception|crash|failed|fatal/.test(corpus)) return false;
  return true;
});

const timestampHeureDansJournee = (heure, journee, timestampReference) => {
  if (!/^\d{2}:\d{2}$/.test(String(heure || ''))) return 0;
  const jour = /^\d{4}-\d{2}-\d{2}$/.test(String(journee || ''))
    ? journee
    : jourIsoDepuisTimestamp(timestampReference);
  return timestampMs(`${jour}T${heure}:00`);
};

const jourIsoDepuisTimestamp = (valeur) => {
  const ts = timestampMs(valeur);
  if (!ts) return new Date().toISOString().slice(0, 10);
  return new Date(ts).toISOString().slice(0, 10);
};

const regrouperTimelineCamera = (timeline, cameraFallback) => {
  const lignesTriees = [...(timeline || [])]
    .map((ligne, index) => ({ ligne, index, ts: timestampMs(ligne.timestamp) }))
    .sort((a, b) => (a.ts || 0) - (b.ts || 0));
  const groupes = [];
  let courant = null;

  lignesTriees.forEach((item) => {
    const camera = cameraDepuisLigneRuntime(item.ligne) || cameraFallback || 'inconnue';
    const famille = familleRuntime(item.ligne);
    const memeEpisode = courant
      && courant.camera === camera
      && item.ts
      && courant.finMs
      && item.ts - courant.finMs <= FENETRE_EPISODE_CAMERA_MS
      && item.ts - courant.debutMs <= DUREE_MAX_EPISODE_CAMERA_MS
      && famillesCompatibles(courant.familles, famille);

    if (!memeEpisode) {
      courant = {
        camera,
        debutMs: item.ts || 0,
        finMs: item.ts || 0,
        familles: new Set(),
        items: [],
      };
      groupes.push(courant);
    }

    courant.items.push(item.ligne);
    courant.familles.add(famille);
    courant.finMs = item.ts || courant.finMs;
  });

  return groupes.map((groupe, index) => construireEpisodeRuntime(groupe, index)).sort((a, b) => (b.finMs || 0) - (a.finMs || 0));
};

const construireEpisodeRuntime = (groupe, index) => {
  const familles = Array.from(groupe.familles);
  const patterns = compacterEvenementsRuntime(groupe.items);
  const famillePrincipale = famillePrincipaleRuntime(familles);
  return {
    id: `episode-camera-${groupe.camera}-${groupe.debutMs || index}`,
    camera: groupe.camera,
    debut: groupe.debutMs,
    fin: groupe.finMs,
    finMs: groupe.finMs,
    familles,
    famillePrincipale,
    resume: resumeEpisodeRuntime(familles),
    severite: severiteEpisodeRuntime(groupe.items),
    evenements: groupe.items,
    patterns,
    nbEvenements: groupe.items.length,
    nbPatterns: patterns.length,
    nbCompactsReduits: groupe.items.length - patterns.length,
  };
};

const compacterEvenementsRuntime = (lignes) => {
  const index = new Map();
  (lignes || []).forEach((ligne, position) => {
    const evenement = ligne.event || ligne.evenement_type || 'evenement';
    const famille = familleRuntime(ligne);
    const signature = signaturePatternRuntime(ligne);
    if (!index.has(signature)) {
      index.set(signature, {
        id: `compact-${position}-${signature}`,
        evenement: evenement || famille,
        famille,
        extrait: ligne.contenu_brut,
        premierTimestamp: ligne.timestamp,
        dernierTimestamp: ligne.timestamp,
        exemples: [],
        nb: 0,
      });
    }
    const compact = index.get(signature);
    compact.nb += 1;
    compact.dernierTimestamp = ligne.timestamp || compact.dernierTimestamp;
    if (compact.exemples.length < 2) compact.exemples.push(ligne.contenu_brut);
  });
  return Array.from(index.values()).sort((a, b) => b.nb - a.nb || timestampMs(a.premierTimestamp) - timestampMs(b.premierTimestamp));
};

const signaturePatternRuntime = (ligne) => [
  familleRuntime(ligne),
  ligne.event || ligne.evenement_type || 'evenement',
  ligne.niveau || '',
  ligne.source || '',
  ligne.domaine || '',
].join('|').toLowerCase();

const familleRuntime = (ligne) => {
  const texteLigne = [ligne.event, ligne.evenement_type, ligne.contenu_brut, ligne.domaine].join(' ').toLowerCase();
  if (/recovery|restart|overlap|deferred/.test(texteLigne)) return 'recovery';
  if (/recording_state|crash|lifecycle|runtime|exception/.test(texteLigne)) return 'runtime';
  if (/convergence|raw|first_bytes|copy|202/.test(texteLigne)) return 'convergence';
  if (/upload|publication/.test(texteLigne)) return 'upload';
  if (/finalize|finalisation/.test(texteLigne)) return 'finalize';
  if (/memory|memoire|storage/.test(texteLigne)) return 'memoire';
  return 'autre';
};

const famillesCompatibles = (famillesCourantes, famille) => {
  if (!famillesCourantes || famillesCourantes.size === 0) return true;
  if (famille === 'autre') return famillesCourantes.size <= 1;
  if (famillesCourantes.has('autre') && famillesCourantes.size > 1) return true;
  const famillesFortes = ['recovery', 'runtime', 'convergence'];
  if (famillesFortes.includes(famille) && Array.from(famillesCourantes).some((f) => famillesFortes.includes(f))) return true;
  return famillesCourantes.has(famille);
};

const famillePrincipaleRuntime = (familles) => {
  const priorite = ['recovery', 'runtime', 'convergence', 'finalize', 'upload', 'memoire', 'autre'];
  return priorite.find((famille) => familles.includes(famille)) || 'autre';
};

const resumeEpisodeRuntime = (familles) => {
  const set = new Set(familles);
  if (set.has('recovery') && set.has('convergence')) return 'recovery/convergence';
  if (set.has('recovery') && set.has('runtime')) return 'recovery/runtime';
  if (set.has('runtime') && set.has('convergence')) return 'runtime/convergence';
  if (set.has('recovery')) return 'recovery';
  if (set.has('runtime')) return 'runtime';
  if (set.has('convergence')) return 'convergence';
  if (set.has('finalize')) return 'finalisation';
  if (set.has('upload')) return 'upload';
  if (set.has('memoire')) return 'memoire';
  return 'signal isole';
};

const severiteEpisodeRuntime = (lignes) => {
  const corpus = (lignes || []).map((ligne) => [ligne.niveau, ligne.event, ligne.evenement_type, ligne.contenu_brut].join(' ').toLowerCase()).join(' ');
  if (/fatal|critical|critique|crash|exception|error|erreur|2626/.test(corpus)) return 'critique';
  if (/warn|warning|degrade|deferred|overlap|pending|202/.test(corpus)) return 'a surveiller';
  return 'normal';
};

const severitePourClasse = (severite) => {
  if (severite === 'critique') return 'critique';
  if (severite === 'a surveiller') return 'a_surveiller';
  return 'normal';
};

const cameraDepuisLigneRuntime = (ligne) => (
  ligne.cam_id
  || ligne.cameraId
  || ligne.camera_id
  || ligne.tel_id
  || extraireChampDepuisRuntime(ligne.contenu_brut, ['cameraId', 'camera_id', 'cam_id', 'camId', 'tel_id', 'telId'])
);

const extraireChampDepuisRuntime = (texteLigne, champs) => {
  const contenu = String(texteLigne || '');
  for (let index = 0; index < champs.length; index += 1) {
    const champ = champs[index];
    const match = contenu.match(new RegExp(`${champ}\\s*[=:]\\s*["']?([^"',;\\s|]+)`, 'i'));
    if (match && match[1]) return match[1];
  }
  return '';
};

const ligneBruteRuntime = (ligne) => {
  if (!ligne) return '';
  return [
    `[${formaterDate(ligne.timestamp)}]`,
    ligne.niveau,
    ligne.event || ligne.evenement_type,
    [ligne.source, ligne.domaine].filter(Boolean).join('/'),
    ligne.contenu_brut,
  ].filter(Boolean).join(' ');
};

const projectionCanoniqueRuntime = (ligne) => {
  if (!ligne) return {};
  return {
    source: ligne.ligne_id
      ? 'monitoring_log_ligne.payload'
      : 'timelineRuntime.payload',
    ligne_id: ligne.ligne_id,
    fichier_id: ligne.fichier_id,
    evenement_type: ligne.evenement_type || ligne.event,
    ts_event: ligne.ts_event || ligne.timestamp || ligne.recu_at,
    tel_id: ligne.tel_id,
    cam_id: ligne.cam_id,
    remote_path: ligne.remote_path,
    numero_ligne: ligne.numero_ligne,
    payload: ligne.payload || {},
  };
};

const cleLigneRuntime = (ligne, index) => (
  ligne.ligne_id || ligne.hash_ligne || `${ligne.remote_path || 'runtime'}-${ligne.numero_ligne || index}-${ligne.timestamp || index}`
);

const libelleModeFiltre = (mode) => {
  if (mode === 'critique') return 'Critiques seulement';
  if (mode === 'convergence') return 'Convergence';
  if (mode === 'recovery') return 'Recovery';
  if (mode === 'erreur') return 'Erreurs';
  return mode;
};

const formaterDureeMs = (ms) => {
  if (!ms || ms < 1000) return 'instantane';
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3600000) return `${Math.round(ms / 60000)} min`;
  return `${(ms / 3600000).toFixed(1)}h`;
};

const construireAnalyse = (signauxReconnus, timeline, episodesRuntime) => {
  const lignesPourSignaux = (signaux) => {
    const aliases = signaux.flatMap((sig) => [sig.id, sig.nom].map((s) => String(s || '').toUpperCase()).filter(Boolean));
    return (timeline || []).filter((l) => {
      const corpus = [l.event, l.evenement_type, l.contenu_brut].join(' ').toUpperCase();
      return aliases.some((a) => a && corpus.includes(a));
    });
  };

  const episodesPourSignaux = (signaux) => {
    const aliases = signaux.flatMap((sig) => [sig.id, sig.nom].map((s) => String(s || '').toUpperCase()).filter(Boolean));
    return (episodesRuntime || [])
      .filter((ep) => ep.evenements.some((l) => {
        const corpus = [l.event, l.evenement_type, l.contenu_brut].join(' ').toUpperCase();
        return aliases.some((a) => a && corpus.includes(a));
      }))
      .map((ep) => ep.id);
  };

  const cartesSignal = (signauxReconnus || []).map((signal) => {
    const lignes = lignesPourSignaux([signal]);
    const timestamps = lignes.map((l) => l.timestamp).filter(Boolean).sort();
    return {
      signal,
      nbOccurrences: lignes.length,
      premierTimestamp: timestamps[0] || null,
      dernierTimestamp: timestamps[timestamps.length - 1] || null,
      preuveObs: lignes[0] ? tronquer(texte(lignes[0].contenu_brut), 140) : 'n/d',
      episodesImpliques: episodesPourSignaux([signal]),
    };
  });

  const bouclesMap = {};
  (signauxReconnus || []).forEach((signal) => {
    filtrerNonDocumente(signal.boucles_associees || []).forEach((b) => {
      const cle = String(b || '').trim();
      if (!cle) return;
      if (!bouclesMap[cle]) bouclesMap[cle] = [];
      bouclesMap[cle].push(signal);
    });
  });
  const cartesBoucle = Object.entries(bouclesMap)
    .sort((a, b) => b[1].length - a[1].length)
    .map(([nom, signaux]) => {
      const lignes = lignesPourSignaux(signaux);
      const timestamps = lignes.map((l) => l.timestamp).filter(Boolean).sort();
      const dureeMs = timestamps.length >= 2
        ? timestampMs(timestamps[timestamps.length - 1]) - timestampMs(timestamps[0])
        : 0;
      return {
        nom,
        signaux,
        dureeMs,
        dureePhraseFormatee: formaterDureeMs(dureeMs),
        episodesImpliques: episodesPourSignaux(signaux),
      };
    });

  const fluxMap = {};
  (signauxReconnus || []).forEach((signal) => {
    (signal.flux_affectes || []).forEach((f) => {
      const cle = String(f || '').trim();
      if (!cle) return;
      if (!fluxMap[cle]) fluxMap[cle] = [];
      fluxMap[cle].push(signal);
    });
  });
  const cartesFlux = Object.entries(fluxMap)
    .sort((a, b) => b[1].length - a[1].length)
    .map(([nom, signaux]) => {
      const criticiteMax = signaux.reduce((max, sig) => {
        const c = String(sig.criticite || '').toLowerCase();
        if (c === 'critique' || c === 'rouge') return 'critique';
        if (max !== 'critique' && (c === 'orange' || c === 'jaune')) return 'degrade';
        return max;
      }, 'normal');
      return { nom, signaux, criticiteMax, episodesImpliques: episodesPourSignaux(signaux) };
    });

  return { cartesSignal, cartesBoucle, cartesFlux };
};

export default CockpitInvestigation;



// WEBPACK FOOTER //
// src/components/cockpit/CockpitInvestigation.js